import { webUrlFromPointer } from '../models/web-url-pointer';
import { ViewType } from '../utils/ui/view-types';
import { bindAssetEditorRegistry } from '../models/asset-editor';
import { EventEmitter } from 'events';
import { v4 as uuidv4 } from 'uuid';
import { ApiError, ApiWarning, isApiError } from '../ApiResponse';
import apiClient, { apiStats, clearStats, GRAPH_API_PREFIX, ListPage, pageOf, withPage } from '../client';

import config from '../config';
import { IEntity } from '../IEntity';
import type { AssetOccurrence } from '../APIEntity';
import { ActionInfo } from '../models/ActionInfo';
import type { ReturnedValue } from '../models/ReturnedValue';
import { readFlowAnswer, readFlowEnvelope } from './flow-call';
import { isAxiosError } from 'axios';
import { BootstrapInfo, DeferredInfo, ScanInfo } from '../models/BootstrapInfo';
import { isValidIdentifier, isValidEntityId, TypeId } from '../models/TypeId';
import { dockOptionsToScopeFilter } from '../utils/scope-filter';
import { isHubOnly } from '../utils/hub-runtime';
import { isElectronShell } from '../utils/runtime';
import { isAbsoluteMachinePath } from '../utils/vfs-path';
import { parseWikiPointer } from '../utils/wiki-word';
import { UserRole } from '../models/Membership';
import {
  ConnectionManager,
  ControlMessage,
  DataOpType,
  OAuthMessage,
  PtyOutputMessage,
  RestApiMessage,
} from '../websocket';
import { FlowData, FlowDataAttribute, FlowDataSource } from '../flow_processing';
import { toplog } from '../services/toplog';
import { getUtmParams } from '../utils/utm';
import { emitEntityTag } from './entity.onTag';
import { ExpansionType } from './expand';
import { EntityFactory } from '../schema/factory';
import { SubscriptionMap, TypeIdMap, WatchMap, WatchQueryMap } from './map';
import { ExpansionRequest, QueryRequest, WatchedQuery } from './query';
import { ActionType, JSONSchemaParser, TypeInfo } from './schema';
import { ptyOrphanBuffer } from '../services/shell/ptyOrphanBuffer';

/** Whether `fetch` can resolve what this client addresses. It parses the URL itself
 *  and rejects a relative one — no document is consulted — so the base has to be
 *  absolute, which it is wherever the app really runs. */
function canFetchAbsolute(): boolean {
  return /^https?:\/\//i.test(apiClient.defaults.baseURL ?? '');
}

export enum EntityStatus {
  NA = 'NA',
  FETCHING = 'FETCHING',
  READY = 'READY',
  ERROR = 'ERROR',
}

export interface EntityExpansion {
  roles?: UserRole[] | null;
  allowed_actions?: ActionType[] | null;
  auth_scopes?: string[][] | null;
  is_private?: boolean;
  expansions?: ExpansionType[] | null;
  /** Is this row the caller's own (llm_endpoint: an allowance they hold)? Rides `permissions`. */
  mine?: boolean | null;
  /** What this row draws from, in fallback order (llm_endpoint). Rides `permissions`. */
  source_ids?: string[] | null;
  /** Who sponsors the row (llm_endpoint: `global` | `organization` | `team` | `user`). */
  scope_kind?: string | null;
  /** The row's own limits with used / remaining / resets_at (llm_endpoint). */
  remaining?: RemainingEntry[] | null;
  /** The model each size (sm/md/lg) resolves to down the row's chain (llm_endpoint); `null` = none declared. */
  tiers?: Record<string, string | null> | null;
}

/** One limit of a row with what is used of it, as the hub reports it. */
export interface RemainingEntry {
  key: string;
  limit: number;
  used: number;
  remaining: number;
  window: string;
  resets_at: number | null;
}

interface PendingPromise<T> {
  resolve: (value: T | null) => void;
  reject: (reason: any) => void;
}

/** One minute of inactivity before a user edit is stamped on the backend. */
export const EDIT_MARK_DEBOUNCE_MS = 60_000;

export interface RecentEntityEdit {
  target: TypeId;
  markedAt: number;
}

const RECENT_ENTITY_EDITS_CHANGED = 'recent_entity_edits_changed';

interface PendingEditMark {
  target: TypeId;
  revision: number;
  dueAt: number;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: Promise<void> | null;
}

/**
 * One trailing edit marker per logical entity in this DataManager realm.
 *
 * APIEntity objects can be re-hydrated or addressed through a uname alias, so
 * timers cannot live on an individual object. The caller supplies the
 * canonical type + entity UUID and this coordinator owns the debounce and
 * request serialization without mutating the cached entity locally.
 */
class EntityEditMarker {
  private pending = new TypeIdMap<PendingEditMark>();
  private recent = new TypeIdMap<RecentEntityEdit>();
  private recentSnapshot: readonly RecentEntityEdit[] = [];

  constructor(
    private readonly send: (target: TypeId) => Promise<unknown>,
    private readonly onRecentChanged: () => void,
  ) {}

  getRecentSnapshot(): readonly RecentEntityEdit[] {
    return this.recentSnapshot;
  }

  mark(target: TypeId): void {
    let state = this.pending.get(target);
    if (!state) {
      const canonicalTarget = new TypeId(target);
      state = {
        target: canonicalTarget,
        revision: 0,
        dueAt: 0,
        timer: null,
        inFlight: null,
      };
      this.pending.set(canonicalTarget, state);
    }

    state.revision += 1;
    const markedAt = Date.now();
    state.dueAt = markedAt + EDIT_MARK_DEBOUNCE_MS;
    this.recent.set(state.target, { target: state.target, markedAt });
    this.recentSnapshot = [...this.recent.values()].sort((a, b) => b.markedAt - a.markedAt);
    this.onRecentChanged();
    this.arm(state);
  }

  reset(): void {
    for (const state of this.pending.values()) {
      if (state.timer) clearTimeout(state.timer);
    }
    this.pending.clear();
    if (this.recentSnapshot.length > 0) {
      this.recent.clear();
      this.recentSnapshot = [];
      this.onRecentChanged();
    }
  }

  private arm(state: PendingEditMark): void {
    if (state.timer) clearTimeout(state.timer);
    state.timer = setTimeout(
      () => {
        state.timer = null;
        void this.flush(state);
      },
      Math.max(0, state.dueAt - Date.now()),
    );
  }

  private async flush(state: PendingEditMark): Promise<void> {
    if (this.pending.get(state.target) !== state) return;

    // A newer mark may reach its deadline while the prior request is still
    // running. Its revision remains pending; the prior request's finally block
    // re-arms it without ever overlapping two writes for this entity.
    if (state.inFlight) return;

    const sentRevision = state.revision;
    const request = Promise.resolve()
      .then(() => this.send(state.target))
      .then(() => undefined);
    state.inFlight = request;

    try {
      await request;
    } catch (error) {
      // Edit recency is best-effort. Do not retry a failed generation: a later
      // real user edit creates a fresh trailing window, avoiding request loops.
      console.warn(`[EntityEditMarker] Failed to mark ${state.target.toString()} as edited:`, error);
    } finally {
      // reset() may have discarded this state while its request was in flight.
      if (this.pending.get(state.target) !== state) return;
      state.inFlight = null;
      if (state.revision > sentRevision) this.arm(state);
      else this.pending.delete(state.target);
    }
  }
}

class EntityRef<T> {
  status: EntityStatus = EntityStatus.NA;
  error: ApiError | null = null;
  entity: T | null = null;
  /**
   * Terminal "the backend 404'd this id" marker. Set when a by-typeid GET
   * returns 404 so the read path can short-circuit subsequent fetches instead
   * of re-hitting the network on every re-subscribe (the dangling
   * context-entity-chip 404 loop). Cleared whenever the ref is dropped via
   * ``invalidateCacheByTypeId`` / ``removeEntityFromCache`` (a later WS arrival
   * or an explicit re-resolve), so a since-materialized entity self-heals.
   */
  notFound: boolean = false;
  entityPendingPromises: PendingPromise<T>[] = [];
  /** Latest WS state received while an HTTP read/save owns this ref. */
  pendingUpdate: IEntity | null = null;
  /**
   * Save serialization is independent of ``status``. Status is observable
   * cache state and can be touched by reads/notifications; it must never be
   * the mutex that decides whether two writes may overlap.
   */
  saveTail: Promise<void> = Promise.resolve();
  saveInFlight: boolean = false;

  constructor(entity: T | null = null) {
    this.entity = entity;
  }
}

export interface Manageable {
  typeId: TypeId;
  dirty: boolean;
  get saved(): boolean;
  load(): Promise<void>;
  toJSON(): any;
  expand?: EntityExpansion;
  /** Mirrors ``APIEntity.remote`` — true once the row is hub-backed. The store
   *  reads it to decide whether a save carries the ``Hub-Reflect`` header. */
  remote?: boolean;
  /** Mirrors ``APIEntity.asset_occurrences`` — the backend's complete list of
   *  live paths for this asset identity; the store replaces it wholesale. */
  asset_occurrences?: AssetOccurrence[];
  isExpanded(expansion: ExpansionType | ExpansionType[] | ExpansionRequest): boolean;
  isDbField(fieldName: string): boolean;
  /** True once the row is finished for the UI (a closed tab, a closed shell): the store
   *  may drop it when nothing references it. Absent means never. */
  isRetired?(): boolean;
  /** A mounted view listens to the entity or its stream — a reference the store cannot count. */
  hasListeners?(): boolean;
  /** Called when the store drops the entity for good; it frees what hangs off it. */
  onEvicted?(): void;
}

/**
 * Types we have already reported as having no client entity constructor. Bounded
 * by the number of backend types, and never reset: the answer cannot change
 * within a session because the entity registry is populated at import.
 */
const loggedMissingCtorTypes = new Set<string>();

export class DataManager<T extends Manageable> extends EventEmitter {
  entities: TypeIdMap<EntityRef<T>> = new TypeIdMap<EntityRef<T>>();

  schemas: { [type: string]: JSONSchemaParser } = {};
  /**
   * Frontend SchemaRegistry — complete reflection of the backend type registry
   * (TypeInfo + nested JSON schema), populated once from the bootstrap ``types``
   * payload via {@link loadTypes}. Single source of truth for type metadata
   * (icon/browseable_by/creatable/fields) and validation schemas.
   */
  typeInfos: { [type: string]: TypeInfo } = {};
  saveIntervalMs: number = 5000;
  isPopupOpen = false;
  dataOpQueryInvalidation = false;
  private subscriptions: SubscriptionMap<T> = new SubscriptionMap<T>();
  private _inFlightGets: Map<string, Promise<unknown>> = new Map();
  private watches: WatchMap = new WatchMap();
  /** The `/watch` request open for a key, if any (see `postWatch`). */
  private watchPosts = new TypeIdMap<Promise<void>>();
  private readScope: string | undefined;
  private queryEpoch = 0;

  /** Do not let a previous identity's pending query populate the new identity's entity store. */
  public adoptReadScope(scope: string): void {
    if (scope === this.readScope) return;
    this.queryEpoch++;
    this.watchedQueries = new WatchQueryMap<T>();
    if (this.readScope !== undefined) void this.clearCache();
    this.readScope = scope;
  }

  private watchedQueries: WatchQueryMap<T> = new WatchQueryMap<T>();
  /** One bound reference per connection event: on() and off() must be given the same function. */
  private readonly connectionHandlers: ReadonlyArray<readonly [string, (...args: any[]) => void]> = [
    ['on_open', this.onConnectionOpen.bind(this)],
    ['on_close', this.onConnectionClose.bind(this)],
    ['on_data_op', this.onDataOp.bind(this)],
    ['on_control_msg', this.onControlMessage.bind(this)],
    ['on_oauth_msg', this.onOAuthMessage.bind(this)],
    ['on_pty_output_msg', this.onPtyOutputMessage.bind(this)],
    ['on_flow_data', this.onFlowData.bind(this)],
  ];
  /** Managers this store listens to; attaching one twice would deliver every event twice. */
  private readonly attachedManagers = new Set<ConnectionManager>();
  private streamingRequestsCount: number = 0;
  private editMarker: EntityEditMarker;
  scanInfo: ScanInfo | null = null;
  recordsRoot: string | null = null;

  constructor() {
    super();
    this.editMarker = new EntityEditMarker(
      async (target) => {
        // Local recency only in v1. A later explicit share can carry the stored
        // field, but this delayed marker does not reflect an action to the Hub.
        const action = new ActionInfo('mark-edit', target.type, target.id, 'POST');
        const ref = this.getRef(target);
        await this.enqueueEntityWrite(ref, () => this.callAction<undefined, unknown>(action));
      },
      () => this.emit(RECENT_ENTITY_EDITS_CHANGED),
    );
    this.attach_connection_manager(ConnectionManager.getInstance());
    // Schedule the repeated function call every 5 seconds
    //setInterval(() => this.onSaveAllDirty(), this.saveIntervalMs);
  }

  public attach_connection_manager(manager: ConnectionManager) {
    if (this.attachedManagers.has(manager)) return;
    this.attachedManagers.add(manager);
    for (const [event, handler] of this.connectionHandlers) manager.on(event, handler);
  }

  public detach_connection_manager(manager: ConnectionManager) {
    if (!this.attachedManagers.delete(manager)) return;
    for (const [event, handler] of this.connectionHandlers) manager.off(event, handler);
  }

  public getSchema(type: string): JSONSchemaParser {
    return this.schemas[type.toLowerCase()];
  }

  /** TypeInfo for a type (icon/browseable_by/creatable/fields/schema). */
  public getTypeInfo(type: string): TypeInfo | undefined {
    if (typeof type !== 'string') return undefined;
    return this.typeInfos[type.toLowerCase()];
  }

  /** Types a query was answered empty for because the server does not serve them (warned once). */
  private readonly unservedTypesLogged = new Set<string>();

  /** Whether the connected server serves rows of this type — its bootstrap ``types`` lists every
   *  type it serves (the desktop all of its own, the hub its API-visible ones). Before the types
   *  load nothing is known, so nothing is refused. */
  public servesType(type: string): boolean {
    if (Object.keys(this.typeInfos).length === 0) return true;
    return typeof type === 'string' && type.toLowerCase() in this.typeInfos;
  }

  /** Which fields the connected server keeps on rows of `type`: the ones its bootstrap schema lists. Each
   *  server lists exactly the fields it keeps, so a query is folded to those before it is sent
   *  (`QueryFilter.forServer`). Before the types load — or for a schema that does not even list `id`, so does
   *  not describe its rows — nothing is known, and every field counts as kept. */
  private keptFields(type: string): (field: string) => boolean {
    const fields = this.getSchema(type)?.fieldSet;
    return fields?.has('id') ? (field) => fields.has(field) : () => true;
  }

  /** All registered TypeInfos (the frontend SchemaRegistry). */
  public getAllTypeInfos(): TypeInfo[] {
    return Object.values(this.typeInfos);
  }

  /**
   * Single source of truth for a type's icon — the lucide icon name authored on
   * the backend (TypeInfo.icon). Null when unknown / icon-less; callers fall
   * back to a generic icon.
   */
  public iconForType(type: string): string | null {
    if (typeof type !== 'string') return null;
    return this.typeInfos[type.toLowerCase()]?.icon ?? null;
  }

  /**
   * Single source of truth for a type's UX-friendly label — the backend-authored
   * ``TypeInfo.display_name``. Null when unknown / unlabeled; callers fall back to
   * ``humanizeType`` (see `labelForType` in the app).
   */
  public displayNameForType(type: string): string | null {
    if (typeof type !== 'string') return null;
    return this.typeInfos[type.toLowerCase()]?.display_name ?? null;
  }

  /**
   * Load the bootstrap ``types`` payload into the frontend SchemaRegistry.
   * Populates {@link typeInfos} and, for entity-backed types, derives the
   * {@link schemas} (JSONSchemaParser) map so getSchema()/APIEntity validation
   * keep working off the same single channel. Replaces the former loadSchemas.
   */
  public async loadTypes(types?: TypeInfo[]) {
    if (!Array.isArray(types)) {
      try {
        types = await apiClient.get<TypeInfo[]>(config.API_PREFIXES.schema);
        if (!types) {
          return null;
        }
      } catch (error) {
        console.error('Error loading types', error);
        throw error;
      }
    }
    // The registry is the authority for type → editor and per-type extensions
    // (`editorForType`, `editorForPath`, `mainFileForType`); bind it once the
    // payload is in hand so the model layer reads live TypeInfos, not a copy.
    bindAssetEditorRegistry({
      get: (type) => this.getTypeInfo(type),
      all: () => this.getAllTypeInfos(),
    });
    for (const typeInfo of types) {
      if (!typeInfo?.type_name) {
        console.warn('TypeInfo has no type_name', typeInfo);
        continue;
      }
      this.typeInfos[typeInfo.type_name.toLowerCase()] = typeInfo;
      if (typeInfo.schema) {
        const schema = new JSONSchemaParser(typeInfo.schema);
        const schemaKey = schema.entity_type ?? typeInfo.type_name.toLowerCase();
        if (!this.schemas[schemaKey]) {
          this.schemas[schemaKey] = schema;
        }
      }
    }
    return types;
  }

  setScanInfo(info: ScanInfo): void {
    this.scanInfo = info;
    this.emit('scan_info_changed', info);
  }

  onScanInfoChange(callback: (info: ScanInfo) => void): () => void {
    this.on('scan_info_changed', callback);
    return () => this.off('scan_info_changed', callback);
  }

  async refreshScanInfo(): Promise<void> {
    // Hub mode: the hub backend has no local fs-records `/index-status` (404).
    // Seed an empty (idle, never-indexed) scan info instead of round-tripping.
    if (isHubOnly()) {
      this.setScanInfo({ total_indexed: 0, last_indexed_at: null, never_indexed: true, stale: false });
      return;
    }
    try {
      // Loaded here, not at module scope: `lazy/assets` reaches `dataManager` (i.e.
      // `APIEntity`), and this module loads inside APIEntity's own load — an eager
      // import would put the base class in a cycle with itself, which is what made
      // `class X extends APIEntity` see `undefined`. See
      // ui/tests/unit/sdk-module-layering.test.ts.
      const { lazyAssets, LazyAsset } = await import('../lazy');
      const raw = await lazyAssets.refresh(LazyAsset.IndexStatus);
      this.setScanInfo({
        total_indexed: raw?.per_type?.reduce((s: number, t: any) => s + (t.entity_count ?? 0), 0) ?? 0,
        last_indexed_at: raw?.last_indexed_at ?? null,
        never_indexed: raw?.never_indexed ?? true,
        stale: raw?.stale ?? false,
      });
    } catch {
      /* non-fatal */
    }
  }

  public async bootstrap(domain?: string, session?: boolean): Promise<BootstrapInfo> {
    const actionInfo = new ActionInfo('bootstrap');
    const queryParams: any = {};

    if (domain) {
      queryParams.domain = domain;
    }
    if (session !== undefined) {
      queryParams.session = session;
    }

    // The server decides the runtime kind; this is the one input only the
    // client can observe (the preload bridge). Sent on every bootstrap rather
    // than stored server-side, because one local server serves both the
    // Electron shell and localhost browser tabs and must answer each correctly.
    queryParams.electron = isElectronShell();

    // Add UTM params from current URL
    const utmParams = getUtmParams();
    Object.assign(queryParams, utmParams);

    if (Object.keys(queryParams).length > 0) {
      actionInfo.queryParameters = queryParams;
    }

    try {
      const info = await this.callAction<null, BootstrapInfo>(actionInfo);
      if (info.scan_info) this.setScanInfo(info.scan_info);
      if (info.records_root) this.recordsRoot = info.records_root;
      return info;
    } catch (error: any) {
      console.error('Error calling bootstrap action:', error);
      // Re-throw all errors so they can be handled by initSdk and displayed to the user
      throw error;
    }
  }

  /** Seed optional index status only if no newer status arrived during discovery. */
  public async info(): Promise<DeferredInfo> {
    const scanBeforeRequest = this.scanInfo;
    const info = await this.callAction<null, DeferredInfo>(new ActionInfo('info'));
    if (info.scan_info && this.scanInfo === scanBeforeRequest) this.setScanInfo(info.scan_info);
    return info;
  }

  get schemaLoaded() {
    return Object.keys(this.schemas).length > 0;
  }

  private onConnectionOpen() {
    // The backend lost its watch registrations with the socket: register every watched key
    // again under the new connection_id, once per key. The counts stay — they are the
    // number of holders, and each holder still has its own release to call.
    for (const typeId of Array.from(this.watches.keys())) {
      void this.postWatch(typeId);
    }
  }

  private onConnectionClose() {
    // Reconnection is handled solely by ConnectionManager.reconnect().
    // Do not call connect() here — a second caller races with ConnectionManager
    // and creates duplicate WebSocket instances (same connection_id, two sockets).
  }
  private onControlMessage(data: ControlMessage) {
    this.isPopupOpen = data.state;
  }
  private onOAuthMessage(data: OAuthMessage) {
    this.emit('on_oauth_msg', data);
  }
  private onPtyOutputMessage(msg: PtyOutputMessage): void {
    const shellId = (msg as any).shell_id ?? (msg as any).session_id ?? '';
    if (!shellId) return;
    const typeId = new TypeId('shell', shellId);
    const ref = this.entities.get(typeId);
    const shell = ref?.entity as any;
    if (shell?.ptyConnection) {
      // Fast path: route directly through PtyConnection (always present on Shell).
      const decoded = shell.ptyConnection.routeOutput(msg.data ?? '', msg.seq, msg.timestamp_ms);
      if (decoded !== null) {
        this.emit('on_pty_decoded', shellId, decoded);
      }
    } else if (typeof shell?.routePtyOutput === 'function') {
      // Compat path: non-Shell entities that implement routePtyOutput.
      const decoded = shell.routePtyOutput(msg.data ?? '', msg.seq, msg.timestamp_ms);
      if (decoded !== null) {
        this.emit('on_pty_decoded', shellId, decoded);
      }
    } else {
      // Shell not yet in entity cache — buffer for when it arrives.
      ptyOrphanBuffer.buffer(shellId, msg.data ?? '', msg.seq, msg.timestamp_ms);
    }
  }

  private onFlowData(typeId: TypeId, flowDataJson: any) {
    // Parse the envelope before cache resolution. Entity events are also
    // surfaced at manager level so late-mounting hosts can key directly on the
    // target TypeId without depending on which entity instance is hydrated.
    const elementType = flowDataJson.element_type || flowDataJson.elementType || 'notification';
    const attributes = flowDataJson.attributes || {};
    if (elementType === 'entity_event') {
      const event = String(attributes.event ?? '');
      const payload = (attributes.payload as Record<string, unknown>) ?? {};
      this.emit('on_entity_event', typeId, event, payload);

      const entity = this.getByTypeIdFromCache<T>(typeId);
      if (!entity) {
        console.debug(`[DataManager.onFlowData] Entity not found in cache for typeId: ${typeId.toString()}`);
        return;
      }
      if (typeof (entity as any).onEntityEvent === 'function') {
        (entity as any).onEntityEvent(event, payload);
      }
      return;
    }

    // Get entity from cache
    const entity = this.getByTypeIdFromCache<T>(typeId);
    if (!entity) {
      console.debug(`[DataManager.onFlowData] Entity not found in cache for typeId: ${typeId.toString()}`);
      return;
    }

    // Create FlowData from JSON
    // Backend sends content as 'flow_value', fallback to 'content' for compatibility
    const content = flowDataJson.flow_value ?? flowDataJson.content ?? '';

    // Prefer the ORIGINATING time when the emitter carried one. The WS layer
    // stamps its own send-time `t` over the caller's attributes
    // (`send_flow_data_to_entity`), so for a transcript-sourced row `t` is when
    // the frame was broadcast, not when the agent did the thing — which would
    // sort a pushed row after its own history twin. Same `created_time` → `t`
    // mapping `FlowData.fromJSON` applies on the history path.
    const originatedAt = (flowDataJson as { created_time?: unknown }).created_time;
    if (typeof originatedAt === 'string' && originatedAt) {
      attributes['t'] = originatedAt;
    } else if (!attributes['t']) {
      attributes['t'] = new Date().toISOString();
    }
    const wireIndex = (flowDataJson as { index?: unknown }).index;
    if (wireIndex !== undefined && !attributes['i']) attributes['i'] = String(wireIndex);

    const flowData = new FlowData(elementType, content, attributes);
    // The FlowData constructor already reads `attributes['source']` and sets
    // `flowData.source` to the matching FlowDataSource enum value (or
    // FlowDataSource.Unknown when absent). For backend-translated events
    // (sniffer hooks via convert_hook_event, history replay, etc.) the
    // source is set authoritatively upstream, so we respect it here. Only
    // events that don't carry a source attribute (legacy WS-only paths) get
    // tagged as WebSocket.
    if (flowData.source === FlowDataSource.Unknown) {
      flowData.source = FlowDataSource.WebSocket;
    }
    flowData.attributes[FlowDataAttribute.FRONTEND_EV_SOURCE_TYPE] = 'DataManager';

    if (flowData.elementType === 'chat') {
      toplog.log(
        'chat_delivery',
        `from_websocket entity=${typeId.toString()} group=${flowData.groupId ?? 'none'} t=${attributes['t'] ?? 'none'} i=${attributes['i'] ?? 'none'}`,
      );
    }

    // Route to entity's handleFlowData method if it exists
    if (typeof (entity as any).handleFlowData === 'function') {
      (entity as any).handleFlowData(flowData);
    }
  }

  private onDataOp(typeIdStr: string, op: DataOpType, data: IEntity) {
    const typeId = new TypeId(typeIdStr);

    // child_* INVERTS the envelope: `typeId` is the PARENT, `data` is the CHILD.
    // Handle and return before the switch below, which assumes they are the same
    // entity — falling through would run castAndDeepAssign(child) into the
    // parent's cache ref and corrupt it.
    //
    // A child op means "your subtree changed"; the authoritative membership is
    // the parent's own projection, so refresh the parent rather than trying to
    // splice the child in. That refetch is what makes a synced message appear in
    // an already-open conversation without a manual Refresh.
    if (op === 'child_created' || op === 'child_updated' || op === 'child_deleted') {
      emitEntityTag(typeId, op, (data as IEntity) ?? null);
      if (this.hasRef(typeId)) {
        void this.fetchByTypeId(typeId).catch(() => {
          /* best-effort wake-up: the next read repairs a failed refresh */
        });
      }
      return;
    }

    // Skip non-entity data (e.g., flow report elements that have element_type but no id)
    // These are handled by other listeners (e.g., instruction trace handlers)
    // Note: delete operations may have null/empty data, so we only skip for non-delete ops
    if (op !== 'delete' && (!data || !('id' in data))) {
      return;
    }

    const ctor = EntityFactory.getEntityConstructor(typeId.type);
    if (!ctor) {
      // Expected, not exceptional. The backend broadcasts ops for every
      // api-visible type, and a dozen of those are deliberately not modelled as
      // client entities — `helpdesk` reaches the UI as bare actions, and so
      // on. `api_visible` is the
      // only dial the backend has and it also gates the schema payload the UI
      // needs for each type's label and icon, so these frames cannot simply be
      // switched off.
      //
      // Debug, and once per type: an index sweep re-broadcasts every row, which
      // turned this into a warn storm that buried real signal. The type name
      // stays so a genuinely missing constructor is still findable.
      if (!loggedMissingCtorTypes.has(typeId.type)) {
        loggedMissingCtorTypes.add(typeId.type);
        console.debug(`Data op ignored: no client entity for type '${typeId.type}' (expected for server-only types)`);
      }
      return;
    }
    // Bus wake-up BEFORE the branchy cache handling below: several branches
    // early-return (uncached update, in-flight buffer) and must still emit.
    emitEntityTag(typeId, op, data ?? null);
    // Handle delete operation by removing from all query results
    if (op === 'delete') {
      this.watchedQueries.removeEntityFromResults(typeId.type, typeId, this.refillPage);
    } else if (op === 'update' && this.dataOpQueryInvalidation) {
      // `update` ops only invalidate (full network refetch) when explicitly
      // enabled. `create` no longer takes this path — the data-op already carries
      // the full entity, so it is spliced into matching results locally below
      // (see the `case 'create'` block) instead of re-running the LIST query over
      // the network per data-op (X5a). `dataOpQueryInvalidation` is currently
      // never enabled, so this branch is dormant in practice.
      const watchedQueries = this.watchedQueries.getWatchCallbacksByType(typeId.type);

      for (const watchedQuery of watchedQueries) {
        if (!watchedQuery.request.query || watchedQuery.request.query.validate(data)) {
          // Rerun query and update results through WatchedQuery
          void this._query(watchedQuery.request).then((queryResult) => {
            watchedQuery.updateResults(queryResult);
          });
        }
      }
    } else if (op === 'update') {
      // An update can move an entity OUT of (or INTO) a match-filtered live
      // query — e.g. a Tab whose `visible` flips false on close must drop out of
      // the `visible:true` strip query. `create`/`delete` already maintain
      // membership; updates did not, so a soft-closed entity lingered in stale
      // results until some unrelated refetch happened to re-run the query (the
      // intermittent "tab won't close" symptom). Reconcile per query: drop rows
      // that no longer match (local splice, no network); refetch only when a row
      // newly matches so the server still applies scope.
      const watchedQueries = this.watchedQueries.getWatchCallbacksByType(typeId.type);
      for (const watchedQuery of watchedQueries) {
        if (!watchedQuery.results) continue;
        const matches = !watchedQuery.request.query || watchedQuery.request.query.validate(data);
        const index = watchedQuery.results.findIndex((entity: any) => entity.typeId.equals(typeId));
        const inResults = index !== -1;
        if (!matches && inResults) {
          watchedQuery.results.splice(index, 1);
          watchedQuery.notifyCallbacks();
        } else if (matches && !inResults) {
          void this._query(watchedQuery.request).then((queryResult) => {
            watchedQuery.updateResults(queryResult);
          });
        } else if (matches && inResults) {
          // Field update on a row already IN the results: the cache merge below
          // (castAndDeepAssign) mutates the very object held in `results`, so
          // the data is fresh — but without a notify the subscribers never
          // re-render and the value stays stale-in-React (e.g. the `activate`
          // recency stamp never reordering a live list). Local, no network —
          // the symmetric completion of the splice branch above.
          //
          // That merge reaches this row only when the row IS the cached instance.
          // A list can hold its own copy of the row (seeded before the cache
          // ref was minted, or the ref replaced since), and then the merge
          // updates the cache while the list re-renders its stale copy forever —
          // a live session's line stuck on "awaiting approval" after the host
          // approved. Merge into the row the list holds too.
          const row = watchedQuery.results[index];
          if (row !== this.entities.get(typeId)?.entity) this.deepAssign(row, data);
          // An ordered list keeps its order: the changed field may be the one it is sorted by.
          WatchQueryMap.reorder(watchedQuery, { typeId, data });
          watchedQuery.notifyCallbacks();
        }
      }
    }

    switch (op) {
      case 'create': {
        const existingRef = this.entities.get(typeId);
        if (existingRef && (existingRef.saveInFlight || existingRef.status === EntityStatus.FETCHING)) {
          // An in-flight save OR GET owns this ref (same guard as the 'update'
          // branch). Applying the create now would let a slower GET response
          // (whose body predates the create) merge back on top and erase it.
          // Buffer instead; fetchByTypeId/save flush it via applyPendingUpdate
          // once the request resolves, so the create's fields win.
          this.bufferPendingUpdate(existingRef, data);
          // ...but LIST MEMBERSHIP is not a field merge and must not wait for
          // that flush. This is the self-created case — the client called
          // save(), so its own ref is mid-flight — and it is the common one:
          // skipping the splice here left an entity the caller had just created
          // missing from its own live queries until something unrelated
          // refetched. `find-or-create` then queried, did not see the row it had
          // just written, and minted a SECOND project for the same work dir
          // (tests/api/project_id_sync.test.ts).
          //
          // The pre-splice code did a full network LIST refetch from a branch
          // ABOVE this switch, so it ran whatever the ref's state was; moving
          // the work inside the switch is what put it behind this guard.
          //
          // Splices the ref's existing entity, not `data`: that object is the
          // one the caller holds, and applyPendingUpdate merges the buffered
          // create into it in place once the save resolves.
          if (existingRef.entity) {
            this.watchedQueries.insertEntityIntoResults(typeId.type, typeId, existingRef.entity, data);
          }
          break;
        }
        this.applyCreate(typeId, data);
        // Every create is broadcast to every connection. One this page never asked for
        // (no ref before it) and nothing took (no live query, no subscriber) is not kept.
        if (!existingRef) {
          const created = this.entities.get(typeId);
          if (created?.entity && !this.isReferenced(typeId, created)) this._evict(typeId);
        }
        break;
      }
      case 'update': {
        if (!this.hasRef(typeId)) {
          return;
        }
        const ref = this.getRef(typeId);
        if (ref.saveInFlight || ref.status === EntityStatus.FETCHING || !ref.entity) {
          // An HTTP read/save owns this ref. Buffer the update and leave the
          // status FETCHING: marking READY here lets a later save overlap the
          // active request, while merging now lets an older response erase it.
          this.bufferPendingUpdate(ref, data);
          return;
        }
        ref.entity = this.castAndDeepAssign(data);
        ref.status = EntityStatus.READY;
        // Aliases share the same `ref` so cache reads stay consistent automatically;
        // subscribers on alias keys still need to be notified.
        this._notifyAllAliases(typeId, ref.entity, ref.entity);
        // The update may be the one that finished the row (a tab's `visible=false`).
        this.evictIfUnreferenced(typeId);
        break;
      }
      case 'delete': {
        const entity = this._evict(typeId);
        this._notifyAllAliases(typeId, entity, null);
        break;
      }
    }
    this.resolvePendingRequests();
  }

  private bufferPendingUpdate(ref: EntityRef<T>, data: IEntity): void {
    // DataOps are normally full entities, but merging also preserves fields
    // when a producer sends a partial update. Later arrivals win per field.
    ref.pendingUpdate = ref.pendingUpdate ? { ...ref.pendingUpdate, ...data } : { ...data };
  }

  private applyPendingUpdate(typeId: TypeId, ref: EntityRef<T>): boolean {
    if (!ref.pendingUpdate || !ref.entity) return false;
    const pending = ref.pendingUpdate;
    ref.pendingUpdate = null;
    ref.entity = this.castAndDeepAssign(pending);
    this._notifyAllAliases(typeId, ref.entity, ref.entity);
    // The data-op's query notify already ran when it arrived — with the row still holding
    // what the in-flight save sent (a toggle's optimistic `new`), so every list re-rendered
    // that value. The merge above mutates the very object those results hold; notify again
    // or the lists keep the stale render until something unrelated re-renders them.
    for (const watchedQuery of this.watchedQueries.getWatchCallbacksByType(typeId.type)) {
      if (watchedQuery.results?.some((entity: any) => entity.typeId.equals(typeId))) watchedQuery.notifyCallbacks();
    }
    return true;
  }

  async saveAllDirty() {
    for (const entityRef of this.entities.values()) {
      if (entityRef.entity?.saved && entityRef.entity?.dirty) {
        await this.save(entityRef.entity.typeId);
      }
    }
  }

  /**
   * Re-pull everything this client is currently showing, from the server.
   *
   * The data layer is PUSH-ONLY after its seeding fetch (see `watchQuery`): a
   * live query is seeded once and every later change is expected to arrive over
   * the WebSocket. So a client that missed a message — a laptop that slept, a
   * socket that dropped and re-registered its watches without replaying the gap
   * — or one looking at state nothing pushes at all, has no way back to the
   * truth short of reloading the document. This is that way back, and it is the
   * only pull path in the client.
   *
   * Re-runs every live query and re-fetches every cached entity, then notifies
   * subscribers exactly the way an arriving data-op does, so React repaints from
   * server state. Best-effort per item: one entity that 404s (deleted while we
   * held it) must not abandon the rest of the refresh.
   */
  public async refreshAll(): Promise<void> {
    const requests = this.watchedQueries.getAllWatchedQueries().map((watched) => watched.request);
    const typeIds = Array.from(this.entities.keys());

    await Promise.all([
      ...requests.map((request) =>
        this._query(request)
          .then((results) => this.watchedQueries.updateQueryResults(request, results))
          .catch(() => undefined),
      ),
      // refreshByTypeId notifies the entity's subscribers itself.
      ...typeIds.map((typeId) => this.refreshByTypeId(typeId).catch(() => undefined)),
    ]);
  }

  public async clearCache() {
    // A copy: dropping a key also drops its aliases from the map being walked.
    for (const typeId of Array.from(this.entities.keys())) {
      this.subscriptions.get(typeId)?.forEach((cb) => void cb(null));
      this._evict(typeId);
    }
  }

  public async reset() {
    this.editMarker.reset();
    await this.clearCache();
    clearStats();
    this.streamingRequestsCount = 0;
  }

  /** Schedule one server-owned edit stamp after the entity becomes idle. */
  public markEdit(typeId: TypeId): void {
    this.editMarker.mark(typeId);
  }

  /**
   * User edits observed in this client realm, including marks still waiting for
   * their trailing server stamp. This is an ephemeral activity projection; it
   * never mutates the backend-owned ``last_edited_at`` field.
   */
  public getRecentEntityEdits(): readonly RecentEntityEdit[] {
    return this.editMarker.getRecentSnapshot();
  }

  public onRecentEntityEditsChange(callback: () => void): () => void {
    this.on(RECENT_ENTITY_EDITS_CHANGED, callback);
    return () => this.off(RECENT_ENTITY_EDITS_CHANGED, callback);
  }

  get apiStats() {
    const stats = apiStats.clone();
    stats.streamingRequests = this.streamingRequestsCount;
    stats.totalRequests += this.streamingRequestsCount;
    return stats;
  }

  printStats(title?: string): void {
    const stats = this.apiStats;

    // Get all unique methods from successful, failed, and in-flight requests
    const allMethods = new Set([
      ...Object.keys(stats.successfulRequests),
      ...Object.keys(stats.failedRequests),
      ...Object.keys(stats.inFlightRequests),
    ]);

    const tableData: Array<{
      Method: string;
      Successful: number;
      Failed: number;
      InFlight: number;
      Total: number;
    }> = [];

    // Add data for each method
    for (const method of allMethods) {
      const successful = stats.getSuccessfulByMethod(method);
      const failed = stats.getFailedByMethod(method);
      const inFlight = stats.getInFlightByMethod(method);
      tableData.push({
        Method: method,
        Successful: successful,
        Failed: failed,
        InFlight: inFlight,
        Total: successful + failed + inFlight,
      });
    }

    // Sort by method name for consistent output
    tableData.sort((a, b) => a.Method.localeCompare(b.Method));

    // Add streaming requests row if there are any
    if (stats.streamingRequests > 0) {
      tableData.push({
        Method: 'STREAMING',
        Successful: stats.streamingRequests,
        Failed: 0,
        InFlight: 0,
        Total: stats.streamingRequests,
      });
    }

    // Add summary row
    tableData.push({
      Method: '--- TOTAL ---',
      Successful: stats.totalSuccessfulRequests,
      Failed: stats.totalFailedRequests,
      InFlight: stats.totalInFlightRequests,
      Total: stats.totalRequests,
    });

    if (tableData.length <= 1) {
      console.log(title ? `${title}: No API requests recorded` : 'No API requests recorded');
      return;
    }

    if (title) {
      console.log(`\n=== ${title} ===`);
    }
    console.table(tableData);
    console.log(`Total API requests: ${stats.totalRequests}`);
    console.log(
      `Success rate: ${stats.totalRequests > 0 ? (((stats.totalSuccessfulRequests + stats.streamingRequests) / stats.totalRequests) * 100).toFixed(1) : 0}%`,
    );
  }

  public notifyPropertyChanged(typeId: TypeId, property: string) {
    const ref = this.entities.get(typeId);
    if (!ref) {
      console.debug(`Notify skipped (${property}), Entity ${typeId.toString()} not found`);
      return;
    }

    if (ref.entity?.saved) {
      try {
        if (ref.entity.isDbField(property)) {
          ref.entity.dirty = true;
        }
      } catch (_e) {
        // Silently ignore if schema is not available or isDbField check fails
      }
    }
    this._notifyAllAliases(typeId, ref.entity, ref.entity);
  }

  /**
   * Notify all watched queries for an entity's type that results have changed.
   * Use after mutating an entity in-place (e.g. after attachPty wires shell state)
   * so React hooks re-render without waiting for a WS update or re-query.
   */
  public notifyEntityChanged(entity: T): void {
    const type = DataManager.getTypeOfObject(entity);
    if (!type) return;
    const watchedQueries = this.watchedQueries.getWatchCallbacksByType(type);
    for (const watchedQuery of watchedQueries) {
      watchedQuery.notifyCallbacks();
    }
  }

  /**
   * Compute the alternate cache keys for an entity. Entities loaded with both
   * a `uname` and a real UUID `id` are normally cached only under
   * `<type>-@<uname>` (because `APIEntity.identifier` prefers `@uname`). We
   * also mirror them under `<type>-<id>` so consumers that resolve refs by
   * raw UUID — e.g. `process.project_id` → `Project.getByIdFromCache(uuid)` —
   * find the entity. Returns an array to keep room for future alias kinds.
   */
  private _aliasTypeIdsFor(entity: any): TypeId[] {
    if (!entity?.uname || !entity?.id || entity.id === `@${entity.uname}`) return [];
    try {
      return [new TypeId(entity.getType(), entity.id)];
    } catch {
      return [];
    }
  }

  /**
   * Delete a cache entry AND any alias entries pointing to the same entity.
   * Symmetric: works whether the caller passes the primary or an alias key.
   */
  private _deleteWithAliases(typeId: TypeId): void {
    const ref = this.entities.get(typeId);
    this.entities.delete(typeId);
    if (ref?.entity) {
      for (const alias of this._aliasTypeIdsFor(ref.entity)) {
        if (!alias.equals(typeId)) this.entities.delete(alias);
      }
    }
  }

  /**
   * The ONE way an entity leaves the cache for good — a delete, a removal, an identity
   * switch, an eviction. The key and its aliases go, and the entity frees what hangs off
   * it (its flow-data stream, a shell's PTY connection, a process's artifacts): the cache
   * entry is what kept those reachable, so dropping one without the other leaks the rest.
   * A refetch (`invalidateCacheByTypeId`) is NOT a removal — the object may be on screen —
   * and keeps to `_deleteWithAliases`. Returns the entity that left, if there was one.
   */
  private _evict(typeId: TypeId): T | null {
    const entity = this.entities.get(typeId)?.entity ?? null;
    this._deleteWithAliases(typeId);
    entity?.onEvicted?.();
    return entity;
  }

  /**
   * Whether anything still needs this cache entry: a subscriber, a watch, a live query
   * holding the row, a mounted view listening to the entity, or a request that owns the
   * ref. Counted under every key the entity is cached by.
   */
  private isReferenced(typeId: TypeId, ref: EntityRef<T>): boolean {
    if (ref.saveInFlight || ref.status === EntityStatus.FETCHING || ref.pendingUpdate) return true;
    if (ref.entityPendingPromises.length > 0) return true;
    const entity = ref.entity;
    if (entity?.hasListeners?.()) return true;
    const keys = [typeId];
    if (entity) {
      for (const key of [entity.typeId, ...this._aliasTypeIdsFor(entity)]) {
        if (!keys.some((k) => k.equals(key))) keys.push(key);
      }
    }
    for (const key of keys) {
      if ((this.subscriptions.get(key)?.size ?? 0) > 0 || (this.watches.get(key) ?? 0) > 0) return true;
    }
    return this.watchedQueries.holdsEntity(keys);
  }

  /**
   * Drop a cached entity nobody references any more — asked wherever a reason to keep one
   * ends. Only a row its type calls RETIRED (`isRetired`) qualifies, because many readers
   * take a loaded entity from the cache synchronously and a warm visit must ask the
   * backend for nothing. Anything dropped here simply loads again when it is next asked
   * for. There is no cap and no clock: a reference going away is the trigger.
   */
  private evictIfUnreferenced(typeId: TypeId): void {
    const ref = this.entities.get(typeId);
    if (!ref?.entity?.isRetired?.()) return;
    if (this.isReferenced(typeId, ref)) return;
    this._evict(typeId);
  }

  /**
   * Notify subscribers of `typeId` AND of any alias keys for the same entity.
   * Caller must pass the entity (read BEFORE any deletion) so aliases compute
   * correctly even when notifying about a removal (`value === null`).
   */
  private _notifyAllAliases(typeId: TypeId, entity: any | null, value: any | null): void {
    this.subscriptions.get(typeId)?.forEach((cb) => void cb(value));
    if (entity) {
      for (const alias of this._aliasTypeIdsFor(entity)) {
        if (!alias.equals(typeId)) {
          this.subscriptions.get(alias)?.forEach((cb) => void cb(value));
        }
      }
    }
  }

  public register_new_entity(typeId: TypeId, entity: any) {
    const ref = this.getRef(typeId);
    if (ref.entity && ref.entity !== entity) {
      // NB: do NOT pass `new Error().stack` here. Building the stack string is
      // synchronous and surprisingly expensive; in a re-registration storm
      // (the symptom this whole warn exists to catch) it ran hundreds of times
      // during app init, blocking the main thread long enough to starve the
      // FlowSync WS `open` callback past loadShellRoute's 5s connect fence.
      // console.warn already attaches a stack in the devtools console.
      console.warn(`Entity ${typeId.toString()} already registered with different entity`);
    }
    ref.entity = entity;
    ref.status = EntityStatus.READY;
    for (const alias of this._aliasTypeIdsFor(entity)) {
      if (!alias.equals(typeId)) this.entities.set(alias, ref);
    }
  }

  public hasRef(typeId: TypeId): boolean {
    return this.entities.has(typeId);
  }

  public getByTypeIdFromCache<U extends T>(typeId: TypeId): U | null {
    const ref = this.entities.get(typeId);
    if (ref && ref.entity) {
      return ref.entity as U;
    }
    return null;
  }

  /** The first live cached entity matching `predicate` (optionally of one `type`) — no request. */
  public findInCache<U extends T>(predicate: (entity: U) => boolean, type?: string): U | null {
    for (const [typeId, ref] of this.entities.entries()) {
      if (type && typeId.type !== type) continue;
      const entity = ref.entity as U | undefined;
      if (entity && predicate(entity)) return entity;
    }
    return null;
  }

  /**
   * Distinguished initial name for a tab opening on a DockPointer (called ONCE
   * at Tab creation; docs/tab-management.md). Resolves, in order:
   *   - entity pointer (`…/typeid/<type>-<id>` or a bare `<type>-<id>`) → the
   *     entity's `name` from cache;
   *   - vfs pointer (`…/vfs/<path>`) → the file/folder basename;
   *   - wiki pointer (`wiki/<space>/<name>`) → the keyword.
   * Returns null when nothing distinguished resolves (the chip then falls back
   * to the ViewType title).
   */
  public getTabName(
    dock: { viewType?: string; pointer?: string; options?: Record<string, string> } | null | undefined,
  ): string | null {
    // Resolve a cached entity's name by typeid — either a raw `<type>-<id>`
    // string, or a (viewType, bare-id) pair when the dock's type lives in the
    // viewType segment (e.g. /dock/conversation/<uuid>).
    const nameFromCache = (typeOrRaw: string | undefined, id?: string): string | null => {
      if (!typeOrRaw) return null;
      try {
        const tid = id !== undefined ? new TypeId(typeOrRaw, id) : new TypeId(typeOrRaw);
        // Prefer the entity's canonical display label (e.g. a Conversation
        // surfaces its `title` via getDisplayName), falling back to the raw
        // `name` — both for entities with no display override and for plain
        // cached rows that have no `displayName` getter.
        const ent = this.getByTypeIdFromCache(tid) as {
          displayName?: string | null;
          name?: string | null;
          hasSyntheticDisplayName?: boolean;
        } | null;
        // Never adopt the `<type>-<id>` synthetic as a tab name: returning it here
        // would freeze `agentic_process-<id>` into the durable `Tab.name` (backfill
        // only heals a NULL name). Fall back to null so the chip shows the provider
        // label until a real name is stamped onto the entity (backend
        // `stamp_default_name`), which then flows through `displayName`.
        if (ent?.hasSyntheticDisplayName) return null;
        return ent?.displayName ?? ent?.name ?? null;
      } catch {
        return null;
      }
    };
    // A scope-keyed browser (Assets, Explorer) is a single tab per scope — its
    // STORED title follows the SCOPE, not the (in-tab) sub-pointer: single
    // project → "<project>'s Assets"/"…'s Files"; user → "My Assets"/"My Files";
    // global / all / multi-select → null (chip falls back to the registry
    // title). Runs before the empty-pointer guard because a scoped dock
    // normalizes its pointer to ''. (The strip overlays the ACTIVE assets tab
    // with the focused asset's own name + icon at render time — see
    // `useTabStripItems` — so this scope title shows for inactive tabs.)
    if (dock?.viewType === 'assets' || dock?.viewType === 'explorer') {
      const noun = dock.viewType === 'assets' ? 'Assets' : 'Files';
      const scope = dockOptionsToScopeFilter(dock.options);
      if (scope?.mode === 'project' && scope.activeProjectId) {
        const name = nameFromCache('project', scope.activeProjectId);
        return name ? `${name}'s ${noun}` : noun;
      }
      if (scope?.mode === 'user') return `My ${noun}`;
      return null;
    }
    // The Desktop is scope-keyed too: a project-scoped desktop chip is named
    // "<project> Desktop"; the global/user desktop falls back to the registry
    // title ("Desktop").
    if (dock?.viewType === 'desktop') {
      const scope = dockOptionsToScopeFilter(dock.options);
      if (scope?.mode === 'project' && scope.activeProjectId) {
        const name = nameFromCache('project', scope.activeProjectId);
        return name ? `${name} Desktop` : null;
      }
      return null;
    }
    const pointer = dock?.pointer ?? '';
    if (!pointer) return null;
    if (dock?.viewType === ViewType.WEB_APP) {
      const url = webUrlFromPointer(pointer);
      if (url) return new URL(url).host;
    }
    if (dock?.viewType === 'diff' && pointer.startsWith('asset-compare/')) {
      return 'Asset compare';
    }
    const lastSegment = (path: string): string | null =>
      decodeURIComponent(path).split('/').filter(Boolean).pop() ?? null;
    if (dock?.viewType === ViewType.EDITOR) return lastSegment(pointer);
    // 1. entity — asset-editor typeid form, a bare `<type>-<id>` pointer, or a
    //    bare entity id whose type is carried by the dock's viewType.
    if (pointer.includes('/typeid/')) {
      const n = nameFromCache(pointer.split('/typeid/').pop() ?? '');
      if (n) return n;
    } else if (!pointer.includes('/')) {
      const n = nameFromCache(pointer) ?? nameFromCache(dock?.viewType, pointer);
      if (n) return n;
    }
    // 2. vfs — basename of the path.
    if (pointer.includes('/vfs/')) {
      const base = lastSegment(pointer.split('/vfs/').pop() ?? '');
      if (base) return base;
    }
    // 3. wiki — the word the route actually resolves. NOT the last segment:
    //    `wiki/@local/Docs/Child` resolves `Docs`, so naming the tab `Child`
    //    made the tab strip and the address bar disagree about the open page.
    const wiki = parseWikiPointer(pointer) ?? (dock?.viewType === 'wiki' ? parseWikiPointer(`wiki/${pointer}`) : null);
    if (wiki?.word) return wiki.word;
    // 4. lens transcript — the ClaudeSession entity (id = sessionId), fetched
    //    into cache by the tab mint via `dock.targetTypeId`.
    if (dock?.viewType === 'lens') {
      const segs = pointer.split('/').filter(Boolean);
      if (segs[0] === 'claude' && segs[1] === 'transcript' && segs[2] && !segs[3]) {
        const n = nameFromCache('claude_session', segs[2]);
        if (n) return n;
      }
    }
    return null;
  }

  /**
   * True when a prior by-typeid GET proved this id is a 404 (the entity has no
   * local row). Lets the react hooks render a terminal "unavailable" state and
   * stop re-fetching, rather than looping on the dangling reference. Reset when
   * the ref is dropped via invalidate/remove.
   */
  public isNotFound(typeId: TypeId): boolean {
    return this.entities.get(typeId)?.notFound === true;
  }

  public invalidateCacheByTypeId(typeId: TypeId): void {
    this._deleteWithAliases(typeId);
  }

  public removeEntityFromCache(typeId: TypeId): void {
    this.watchedQueries.removeEntityFromResults(typeId.type, typeId, this.refillPage);
    const entity = this._evict(typeId);
    this._notifyAllAliases(typeId, entity, null);
  }

  public getRef(typeId: TypeId): EntityRef<T> {
    let ref = this.entities.get(typeId);
    if (!ref) {
      ref = new EntityRef();
      this.entities.set(typeId, ref);
    }
    return ref;
  }

  public async waitForTypeId(typeId: TypeId): Promise<T | null> {
    const ref = this.getRef(typeId);
    // if (ref.status === 'READY') {
    //   if (!ref.entity) {
    //     throw new Error('Entity not found but ready');
    //   }
    //   return ref.entity as T;
    // }
    // if (ref.status === 'ERROR') {
    //   return null;
    // }
    const p = new Promise<T | null>((resolve, reject) => {
      ref.entityPendingPromises.push({ resolve, reject });
    });
    this.resolvePendingRequests();
    return p;
  }

  /**
   * Settle every parked waiter whose ref has reached READY/ERROR. `ownRef` is
   * the ref the finishing request holds: it may already be gone from the map
   * (clearCache on a read-scope switch, invalidate, remove), and a waiter
   * parked on a dropped ref is otherwise never settled — its promise hangs
   * forever, as the project menu's "Loading…" rows did.
   */
  private resolvePendingRequests(ownRef?: EntityRef<T>) {
    const refs = new Set(this.entities.values());
    if (ownRef) refs.add(ownRef);
    for (const ref of refs) {
      if (ref.status === EntityStatus.READY) {
        ref.entityPendingPromises.forEach((p) => {
          p.resolve(ref.entity);
        });
        ref.entityPendingPromises = [];
      }
      if (ref.status === EntityStatus.ERROR) {
        ref.entityPendingPromises.forEach((p) => {
          // A 404 ("entity is gone") is resolved as null on the direct path
          // (see getByTypeId), but a concurrent waiter that parked while the
          // fetch was in-flight must get the SAME treatment. Rejecting here —
          // with ``ref.error`` that the 404 path never set — surfaced as a
          // thrown ``null`` in useEntity ("Error fetching entity by type ID:
          // <typeId> null"). Resolve notFound waiters with null instead.
          if (ref.notFound) {
            p.resolve(null);
          } else {
            p.reject(ref.error);
          }
        });
        ref.entityPendingPromises = [];
      }
    }
  }

  /**
   * A query that filters `id` by `$IN` fetches exactly those entities. Claim
   * their refs as FETCHING for its lifetime, so a concurrent `getByTypeId` (a
   * row's `useEntity` mounting in the same commit as its batch) waits for the
   * batch instead of issuing its own GET — without this every batch hydrator
   * still paid one GET per row on a cold cache. Only refs nobody else owns are
   * claimed; `settleIdBatch` releases every one of them.
   */
  private claimIdBatch(type: string, query: QueryRequest['query']): Map<EntityRef<T>, TypeId> {
    const claimed = new Map<EntityRef<T>, TypeId>();
    const match = (query?.toJSON() as { filter?: { match?: { op?: string; operands?: unknown[] } } } | undefined)
      ?.filter?.match;
    if (match?.op !== '$IN' || match.operands?.[0] !== 'id' || !Array.isArray(match.operands[1])) return claimed;
    for (const id of match.operands[1]) {
      if (typeof id !== 'string' || !isValidEntityId(id)) continue;
      const typeId = new TypeId(type, id);
      const ref = this.getRef(typeId);
      if (ref.entity || ref.status === EntityStatus.FETCHING || ref.notFound || ref.saveInFlight) continue;
      ref.status = EntityStatus.FETCHING;
      claimed.set(ref, typeId);
    }
    return claimed;
  }

  /**
   * Release refs claimed by `claimIdBatch` — on success, a partial result, an
   * error, or a scope switch alike. A row the batch returned resolves its
   * waiters with the entity; any other goes back to NA and resolves them with
   * null, so `getByTypeId` falls through to its own GET (hint_path self-heal
   * included). Waiters are settled here directly: `resolvePendingRequests` only
   * settles READY/ERROR refs, and a claimed ref can be orphaned when
   * `register_new_entity` re-points its key at an aliased ref.
   */
  private settleIdBatch(claimed: Map<EntityRef<T>, TypeId>): void {
    for (const [ref, typeId] of claimed) {
      const current = this.entities.get(typeId);
      let entity = ref.entity ?? (current && current !== ref ? current.entity : null);
      if (ref.status === EntityStatus.FETCHING) {
        if (ref.entity) {
          ref.status = EntityStatus.READY;
        } else if (ref.pendingUpdate && this.adoptBufferedCreate(typeId, ref)) {
          entity = ref.entity;
        } else {
          ref.status = EntityStatus.NA;
        }
      }
      const waiters = ref.entityPendingPromises;
      ref.entityPendingPromises = [];
      waiters.forEach((p) => p.resolve(entity ?? null));
    }
  }

  /** A `create` data-op that arrived while a batch owned a ref the batch did not
   *  return: register it exactly as the unowned `create` path would. */
  private adoptBufferedCreate(typeId: TypeId, ref: EntityRef<T>): boolean {
    const pending = ref.pendingUpdate as (IEntity & { type?: string; id?: string }) | null;
    if (!pending?.type || !pending.id) return false;
    ref.pendingUpdate = null;
    this.applyCreate(typeId, pending);
    return true;
  }

  /** Register a created entity and splice it into every matching live query. */
  private applyCreate(typeId: TypeId, data: IEntity): void {
    const entity = this.castAndDeepAssign(data);
    this.register_new_entity(typeId, entity);
    // X5a: splice the already-delivered entity into every matching live
    // query locally (mirror of `removeEntityFromResults` for delete) instead
    // of a full network LIST refetch per create data-op. Gated by the same
    // `query.validate(data)` scope check inside the helper.
    this.watchedQueries.insertEntityIntoResults(typeId.type, typeId, entity, data);
    this._notifyAllAliases(typeId, entity, entity);
  }

  private async fetchByTypeId<U extends T>(
    typeId: TypeId,
    expansions: ExpansionRequest | null = null,
  ): Promise<U | null> {
    const ref = this.getRef(typeId);
    ref.status = EntityStatus.FETCHING;
    const entityJson = await apiClient.get(`${GRAPH_API_PREFIX}/${typeId.type}/${typeId.id}`, {
      params: expansions?.toJSON(),
    });

    // The backend signals "no such entity for this id" two ways: a bare 404, OR
    // a 200 carrying a ``{status:'FAIL', data:null}`` envelope (the graph
    // get-by-id route does the latter for an id it can't resolve — e.g. an
    // optional ``@uname`` system-project lookup that isn't visible to the current
    // user, or a since-removed entity still referenced by a stale id). The 404
    // case is already treated as a quiet not-found in ``getByTypeId``; mirror that
    // here for the FAIL-envelope case so an expected miss negative-caches and
    // returns null instead of letting ``castAndDeepAssign`` throw — which would
    // log a console error and reject for what is not an error. (An *optional*
    // lookup spamming the console then trips every "no console errors" assertion.)
    if (!entityJson || !DataManager.getTypeOfObject(entityJson)) {
      ref.status = EntityStatus.ERROR;
      ref.notFound = true;
      return null;
    }

    const entity = this.castAndDeepAssign<U>(entityJson);
    //Load entity if load flag is set in query
    if (expansions?.load && entity) {
      await entity.load();
    }
    ref.entity = entity;
    ref.status = EntityStatus.READY;

    // Apply any update that arrived via WebSocket while the GET was in-flight
    this.applyPendingUpdate(typeId, ref);

    return ref.entity as U | null;
  }

  /**
   * Re-read an entity from the backend, whatever the cache holds — the cache-first
   * `getByTypeId` answers a saved entry from memory, so it can never see a write that
   * reached this client without an entity op (one another process of the instance made:
   * a local agent deployment saves straight to the database, where no socket of the app
   * announces it). The cached object is merged in place and its subscribers notified.
   * `requiredExpansions` joins the expansions the cached entry already carries, so a
   * refresh never strips what a consumer expanded.
   */
  public async refreshByTypeId(typeId: TypeId, requiredExpansions?: ExpansionRequest): Promise<T | null> {
    const ref = this.getRef(typeId);
    if (ref.status === EntityStatus.FETCHING) {
      return await this.waitForTypeId(typeId);
    }
    const expansions = this.mergeExpansionsWithQuery(ref.entity?.expand?.expansions ?? undefined, requiredExpansions);
    const entity = await this.fetchOrNotFound(typeId, expansions ?? null);
    if (entity) this._notifyAllAliases(typeId, entity, entity);
    return entity;
  }

  public async getByTypeId<U extends T>(typeId: TypeId, requiredExpansions?: ExpansionRequest): Promise<U | null> {
    if (!requiredExpansions) {
      requiredExpansions = new ExpansionRequest();
    }
    const ref = this.getRef(typeId);
    const cachedEntity = this.getByTypeIdFromCache<U>(typeId);
    if (cachedEntity) {
      if (!cachedEntity.saved) {
        if (requiredExpansions?.load) {
          await ref.entity?.load();
        }
        return cachedEntity;
      }
      if (cachedEntity.isExpanded(requiredExpansions)) {
        return cachedEntity;
      }
    }
    // A prior fetch already proved this id is a 404. Don't re-hit the network on
    // every re-subscribe — return the cached "gone" result. The ref is dropped
    // (and this flag cleared) by invalidate/remove when the entity may exist
    // again, so this self-heals.
    if (ref.notFound) {
      return null;
    }
    if (ref?.status === EntityStatus.FETCHING) {
      const entity = await this.waitForTypeId(typeId);
      // The in-flight fetch we waited on may have resolved to a 404. Honor the
      // negative cache instead of falling through to a redundant re-fetch.
      if (ref.notFound) {
        return null;
      }
      if (entity && entity.isExpanded(requiredExpansions)) {
        return entity as U;
      }
    }
    requiredExpansions = this.mergeExpansionsWithQuery(ref.entity?.expand?.expansions ?? undefined, requiredExpansions);
    // `fetchByTypeId` already loads when the expansions ask for it.
    return this.fetchOrNotFound<U>(typeId, requiredExpansions ?? null);
  }

  /**
   * `fetchByTypeId` with the store's one not-found rule, shared by `getByTypeId` and
   * `refreshByTypeId`: a 404 negative-caches and answers null; any other failure is
   * recorded on the ref and rethrown. Parked waiters are released either way.
   */
  private async fetchOrNotFound<U extends T>(typeId: TypeId, expansions: ExpansionRequest | null): Promise<U | null> {
    const ref = this.getRef(typeId);
    try {
      return await this.fetchByTypeId<U>(typeId, expansions);
    } catch (error) {
      // A 404 means the referenced entity no longer exists — e.g. a stale
      // project_id carried by an old shell/agentic_process record whose
      // project was since removed. That's an expected "it's gone" case, not a
      // failure: return null quietly (the signature already allows it) instead
      // of logging a console error and throwing. Mirrors the 404→null pattern
      // used by compute-node/agentic-process/shell entity loaders.
      //
      // Detect the 404 by HTTP status DIRECTLY — not via ``isApiError``, which
      // additionally requires the body to carry the ``{status:'FAIL'}`` envelope.
      // The graph endpoint's 404 is a plain FastAPI ``HTTPException`` with no
      // such envelope, so gating on ``isApiError`` let real 404s fall through to
      // the throw below, spamming the console and defeating the negative cache.
      const httpStatus =
        (error as { response?: { status?: number }; status?: number })?.response?.status ??
        (error as { status?: number })?.status;
      if (httpStatus === 404) {
        ref.status = EntityStatus.ERROR;
        ref.notFound = true;
        return null;
      }
      console.error(`store.ts:Error fetching entity by type ID: ${typeId.toString()}`, error);
      ref.status = EntityStatus.ERROR;
      // Set before `finally`: parked waiters are rejected with `ref.error`, not null.
      ref.error = error as ApiError;
      throw error;
    } finally {
      this.resolvePendingRequests(ref);
    }
  }

  private mergeExpansionsWithQuery(
    expansions: ExpansionType[] | undefined,
    query?: ExpansionRequest,
  ): ExpansionRequest | undefined {
    if (expansions && expansions.length > 0) {
      query ??= new ExpansionRequest({});
      query.expand ??= [];
      query.expand = [...new Set([...expansions, ...query.expand])];
    }
    return query;
  }

  public async registerType(entity_type: string): Promise<boolean> {
    const endpoint = `${GRAPH_API_PREFIX}/register_type`;
    return (await apiClient.post<any>(endpoint, {
      entity_type: entity_type,
    })) as any;
  }

  public async save<U extends T>(selfTypeId: TypeId, scope: TypeId[] = [], entityJson?: IEntity): Promise<U> {
    const ref = this.entities.get(selfTypeId);
    if (!ref) {
      throw new Error('Can not create, Entity not defined');
    }
    const entity = ref.entity;
    if (!entity) {
      throw new Error('Can not create, Empty ref entity');
    }
    // APIEntity supplies a snapshot captured at its save() call. Keep the
    // fallback for internal callers such as saveAllDirty, and clone either
    // form before enqueueing so no caller can mutate a queued payload.
    const capturedJson = JSON.parse(JSON.stringify(entityJson ?? entity.toJSON())) as IEntity;
    const capturedScope = [...scope];

    const operation = this.enqueueEntityWrite(ref, () =>
      this.saveSnapshot<U>(ref, selfTypeId, capturedScope, capturedJson),
    );
    return await operation;
  }

  /**
   * Serialize every mutation for one cached entity through the same tail.
   * Each operation still reports its own failure, while the settled void tail
   * keeps a rejection from poisoning later saves or edit-recency stamps.
   */
  private enqueueEntityWrite<R>(ref: EntityRef<T>, write: () => Promise<R>): Promise<R> {
    const operation = ref.saveTail.then(write);
    ref.saveTail = operation.then(
      () => undefined,
      () => undefined,
    );
    return operation;
  }

  private async saveSnapshot<U extends T>(
    ref: EntityRef<T>,
    selfTypeId: TypeId,
    scope: TypeId[],
    entityJson: IEntity,
  ): Promise<U> {
    // A GET/refresh may already own the ref. Save ordering itself is handled
    // by saveTail above; this loop only preserves the existing read-vs-write
    // exclusion contract.
    while (ref.status === EntityStatus.FETCHING) {
      await this.waitForTypeId(selfTypeId).catch(() => {});
    }

    const entity = ref.entity;
    if (!entity) {
      throw new Error('Can not create, Empty ref entity');
    }
    // Optional: a null typeId used to surface as a raw TypeError from inside a
    // function whose whole point is to name the unusable part of the ref.
    const entityType = entity.typeId?.type;
    if (!entityType) {
      throw new Error('Can not create, Entity type not found');
    }
    let scope_path = '';
    for (const parent_type_id of scope) {
      scope_path = `${scope_path}/${parent_type_id.type}/${parent_type_id.id}`;
    }
    ref.saveInFlight = true;
    ref.status = EntityStatus.FETCHING;
    try {
      let newEntityJson: IEntity | null = null;
      if (!entity.saved) {
        const endpoint = `${GRAPH_API_PREFIX}${scope_path}/${entity.typeId.type}`;
        newEntityJson = (await apiClient.post<IEntity>(endpoint, entityJson)) as IEntity;
      } else {
        // Saved entity → always attempt the PUT. Deliberately not gated on
        // ref.status: a stale ERROR from a previous request would otherwise
        // skip both branches and latch every subsequent save() into the
        // 'No data returned' throw below.
        if (!entity.typeId.id) {
          throw new Error('Entity missing id on ref');
        }
        const endpoint = `${GRAPH_API_PREFIX}${scope_path}/${entity.typeId.type}/${entity.typeId.id}`;
        // A remote entity's save goes to the hub: the local server forwards the PUT verbatim
        // and answers with the HUB's row (hub field names, no local-only fields), after folding
        // it into the local row. So the entity is re-read from the local server, which holds
        // both halves. Local-only entities omit the header and save purely locally; a hub row
        // carries no `remote` flag, so a save on the hub page is a plain PUT.
        const reflect = entity.remote === true;
        const reflectConfig = reflect ? { headers: { 'Hub-Reflect': 'true' } } : undefined;
        newEntityJson = (await apiClient.put<IEntity>(endpoint, entityJson, reflectConfig)) as IEntity;
        if (reflect) {
          newEntityJson = ((await apiClient.get<IEntity>(endpoint)) as IEntity) ?? newEntityJson;
        }
      }
      if (!newEntityJson) {
        throw new Error('No data returned');
      }
      ref.entity = this.castAndDeepAssign(newEntityJson);
      // A WS update may have arrived after the request began. Apply it after
      // the HTTP snapshot so the older response cannot erase newer state.
      this.applyPendingUpdate(selfTypeId, ref);
      ref.status = EntityStatus.READY;
      ref.error = null;
      if (ref.entity) {
        ref.entity.dirty = false;
      }
      return ref.entity as U;
    } catch (error) {
      console.error(`Error saving entity by ID: ${selfTypeId.toString()}`, error);
      ref.status = EntityStatus.ERROR;
      if (isApiError(error)) {
        ref.error = error;
        console.log(error.stack);
      }
      // Preserve an authoritative update even when the HTTP transport failed.
      // The save promise still rejects, but the cache no longer discards WS
      // state that arrived while the request was active.
      if (this.applyPendingUpdate(selfTypeId, ref)) {
        ref.status = EntityStatus.READY;
        ref.error = null;
      } else if (entity.saved && entity.typeId.id) {
        // The edit was applied to the cached entity before the PUT; a refused or failed PUT must
        // not leave it showing as if it saved. Put back what the server holds (still rejecting).
        await this.restoreServerCopy(ref, `${GRAPH_API_PREFIX}${scope_path}/${entityType}/${entity.typeId.id}`);
      }
      throw error;
    } finally {
      ref.saveInFlight = false;
      this.resolvePendingRequests(ref);
    }
  }

  /** Replace a ref's entity with the server's copy after a failed save; best-effort — with no
   *  answer the edited value stays (it is still marked dirty and the save already rejected). */
  private async restoreServerCopy(ref: EntityRef<T>, endpoint: string): Promise<void> {
    try {
      const serverJson = (await apiClient.get<IEntity>(endpoint)) as IEntity | null;
      if (!serverJson) return;
      ref.entity = this.castAndDeepAssign(serverJson);
      if (ref.entity) ref.entity.dirty = false;
    } catch {
      // Unreachable too: nothing more honest to show than the unsaved edit.
    }
  }

  private async callStreamingAction<_Req, Res>(actionInfo: ActionInfo): Promise<Res> {
    this.streamingRequestsCount++;
    const endpoint = actionInfo.actionUrl;
    const fullUrl = `${config.SERVER_URL}${endpoint}`;
    // A FormData body (files riding along) sets its own multipart Content-Type.
    const payload = actionInfo.bodyParameters;
    const body = payload instanceof FormData ? payload : JSON.stringify(payload);
    const headers = {
      ...(typeof body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    };
    // @ts-ignore
    if (apiClient.testUserToken) {
      // @ts-ignore
      headers['Authorization'] = `Bearer ${apiClient.testUserToken}`;
    }

    const response = await fetch(fullUrl, {
      method: actionInfo.method,
      headers: headers,
      body: actionInfo.method !== 'GET' ? body : undefined,
      credentials: 'include', // Important for visitor cookies
      signal: actionInfo.abortSignal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle 204 No Content - valid response with no body (e.g., nothing to resume)
    if (response.status === 204) {
      return null as unknown as Res;
    }

    if (!response.body) {
      throw new Error('Response body is null - server may not support streaming');
    }

    // Return the response as-is for streaming processing by the caller
    return response as unknown as Res;
  }

  public async callAction<_Req, Res>(actionInfo: ActionInfo): Promise<Res> {
    // Handle streaming actions with the new helper
    if (actionInfo.isStreaming) {
      return await this.callStreamingAction<_Req, Res>(actionInfo);
    }

    const endpoint = actionInfo.actionUrl;

    let requestConfig: any = actionInfo.abortSignal ? { signal: actionInfo.abortSignal } : undefined;
    if (actionInfo.isRawResponse) {
      requestConfig = {
        ...(requestConfig ?? {}),
        transformResponse: (data: any) => {
          return { data };
        },
        responseType: actionInfo.responseType || undefined,
      };
    }

    // Per-call hub-reflection opt-in: send the `Hub-Reflect` header only to a
    // desktop/local server, which owns the forwarding bridge. In Hub-only mode
    // this client already targets Hub directly, so reflecting again is neither
    // meaningful nor part of the Hub API contract.
    if (actionInfo.hubReflect && !isHubOnly()) {
      requestConfig = {
        ...(requestConfig ?? {}),
        headers: { ...(requestConfig?.headers ?? {}), 'Hub-Reflect': 'true' },
      };
    }

    // Name this tab as the initiator, so the server answers back to it alone
    // (flow_sdk/core/oauth/flows.py). Scoped to the calls that ask for it: a
    // custom header on every request would cost a CORS preflight each time.
    if (actionInfo.carriesInitiator) {
      requestConfig = {
        ...(requestConfig ?? {}),
        headers: { ...(requestConfig?.headers ?? {}), 'X-Flow-Connection-Id': ConnectionManager.getInstance().id },
      };
    }

    if (actionInfo.keepEnvelope) requestConfig = { ...(requestConfig ?? {}), keepEnvelope: true };

    // XHR cannot outlive the page; the fetch adapter can (same client and interceptors).
    // Only where `fetch` can resolve the address: it parses the URL itself and throws on a
    // relative one, while XHR resolves it against the document. With no absolute base and no
    // document to resolve against, keeping the default adapter costs only the keepalive.
    if (actionInfo.keepalive && canFetchAbsolute()) {
      requestConfig = { ...(requestConfig ?? {}), adapter: 'fetch', fetchOptions: { keepalive: true } };
    }

    // In-flight dedup for GETs: share a pending request with concurrent callers
    // (e.g. StrictMode double-invoke, or multiple components mounting at once).
    // Safe because GETs are idempotent. Mutations (POST/PUT/PATCH/DELETE) are never deduped.
    const method = actionInfo.method ?? 'GET';
    const isDedupable = method === 'GET' && !actionInfo.abortSignal;
    if (isDedupable) {
      const key = actionInfo.keepEnvelope ? `envelope:${endpoint}` : endpoint;
      const pending = this._inFlightGets.get(key);
      if (pending) return pending as Promise<Res>;
      const promise = (async () => {
        try {
          const raw = (await apiClient.get<Res>(endpoint, requestConfig)) as unknown as Res;
          return actionInfo.castResponse ? (this.castAndDeepAssign(raw) as unknown as Res) : raw;
        } finally {
          this._inFlightGets.delete(key);
        }
      })();
      this._inFlightGets.set(key, promise);
      return promise;
    }

    let response: Res;

    switch (method) {
      case 'POST':
      case 'PUT':
      case 'PATCH':
        if (!actionInfo.queryParameters) {
          throw new Error(`Can not call ${method} action ${actionInfo.name}, Missing request data`);
        }
        {
          // One body-carrying send per verb — keeps adding a verb to the case
          // labels above the only edit, instead of another ternary level.
          const send = { POST: apiClient.post, PUT: apiClient.put, PATCH: apiClient.patch }[method];
          response = (await send.call(apiClient, endpoint, actionInfo.bodyParameters, requestConfig)) as unknown as Res;
        }
        break;
      case 'DELETE':
        response = (await apiClient.delete<Res>(endpoint, {
          data: actionInfo.bodyParameters,
          ...requestConfig,
        })) as unknown as Res;
        break;
      default:
        // Fallthrough GET that opted out of dedup (e.g. has abortSignal)
        response = (await apiClient.get<Res>(endpoint, requestConfig)) as unknown as Res;
        break;
    }
    return actionInfo.castResponse ? (this.castAndDeepAssign(response) as unknown as Res) : response;
  }

  /** One action over HTTP, answering its `data` together with the envelope's `warnings` (a SUCCESS
   *  answer saying part of the work did not land), which {@link callAction} only logs. */
  public async callActionWithWarnings<Res>(actionInfo: ActionInfo): Promise<{ data: Res; warnings: ApiWarning[] }> {
    actionInfo.keepEnvelope = true;
    const envelope = (await this.callAction<undefined, unknown>(actionInfo)) as {
      data?: Res;
      warnings?: ApiWarning[] | null;
    } | null;
    return { data: envelope?.data as Res, warnings: Array.isArray(envelope?.warnings) ? envelope.warnings : [] };
  }

  /** FlowCall answers survive either transport; expected work outcomes do not throw.
   * Transport is selected once, never retried on the other transport after a send.
   * The older callAction methods retain their existing envelope/error contracts.
   */
  public async callFlowAction<R extends ReturnedValue = ReturnedValue>(
    actionInfo: ActionInfo,
    transport: 'http' | 'ws' | 'prefer-ws' = 'http',
  ): Promise<R> {
    if (actionInfo.isStreaming || actionInfo.isRawResponse) {
      throw new Error('FlowCall requires a JSON answer, not a raw or streaming response');
    }
    if (transport === 'ws' || (transport === 'prefer-ws' && ConnectionManager.getInstance().connected)) {
      if (typeof FormData !== 'undefined' && actionInfo.bodyParameters instanceof FormData) {
        throw new Error('FlowCall multipart bodies require HTTP');
      }
      return readFlowEnvelope(await this.sendOverWS(actionInfo)) as R;
    }
    try {
      return readFlowAnswer(await this.callAction<void, unknown>(actionInfo)) as R;
    } catch (error) {
      // Older admission actions use 409 for a held slot. Do not reinterpret
      // authorization, validation, network, or server errors as work outcomes.
      if (isAxiosError(error) && error.response?.status === 409) {
        const envelope = error.response.data;
        if (envelope?.status === 'FAIL') {
          try {
            return readFlowEnvelope(envelope) as R;
          } catch {
            // Keep the original transport error, including its status and body.
          }
        }
      }
      throw error;
    }
  }

  public async callActionOverWS<_Req, Res>(
    actionInfo: ActionInfo,
    options?: import('../websocket').IWSRestOptions,
  ): Promise<Res> {
    const response = (await this.sendOverWS(actionInfo, options)) as Res;
    return actionInfo.castResponse ? (this.castAndDeepAssign(response) as unknown as Res) : response;
  }

  /** One action over the socket; answers the server's reply content (the whole envelope). */
  private async sendOverWS(actionInfo: ActionInfo, options?: import('../websocket').IWSRestOptions): Promise<unknown> {
    const connectionManager = ConnectionManager.getInstance();

    if (!connectionManager.connected) {
      throw new Error('WebSocket not connected. Cannot call action over WebSocket.');
    }

    const message: RestApiMessage = {
      message_type: 'rest_api_msg',
      message_id: uuidv4(),
      method: actionInfo.method as 'GET' | 'POST' | 'PUT' | 'DELETE',
      scope: actionInfo.scope.map((tid) => ({ type: tid.type, id: tid.id })),
      direct_resource_type: null,
      target_typeid: actionInfo.targetEntity
        ? { type: actionInfo.targetEntity.type, id: actionInfo.targetEntity.id }
        : null,
      action: actionInfo.name,
      sub_path: actionInfo.subpath,
      query_params: actionInfo.queryParameters as Record<string, unknown> | null,
      body: actionInfo.bodyParameters as Record<string, unknown> | null,
      hub_reflect: actionInfo.hubReflect && !isHubOnly(),
      carries_initiator: actionInfo.carriesInitiator,
    };

    return connectionManager.sendRestApiMessage<unknown>(message, options);
  }

  /**
   * Call an action over the WebSocket when the socket is OPEN, otherwise fall
   * back to the REST path. The branch is decided up front from the connection
   * state — there is no post-failure retry, so a non-idempotent mutation can
   * never be sent twice.
   *
   * For non-file mutations (e.g. a text-only message send) the WS hop skips an
   * HTTP round-trip when a live socket already exists; REST keeps the call
   * working when it doesn't. Multipart/file actions must NOT use this — binary
   * bodies don't travel over the WS rest_api_msg channel.
   */
  public async callActionPreferWS<_Req, Res>(
    actionInfo: ActionInfo,
    options?: import('../websocket').IWSRestOptions,
  ): Promise<Res> {
    if (ConnectionManager.getInstance().connected) {
      // Same answer on either transport. The socket carries the whole `ApiResponse` envelope;
      // `apiClient` unwraps it to `data` and throws on FAIL — do the same before any cast.
      let data = (await this.sendOverWS(actionInfo, options)) as Res;
      const envelope = data as { status?: unknown; message?: unknown; data?: unknown } | null;
      if (envelope?.status === 'FAIL') throw new Error(String(envelope.message ?? 'Request failed'));
      if (envelope?.status === 'SUCCESS') data = envelope.data as Res;
      return actionInfo.castResponse ? (this.castAndDeepAssign(data as never) as unknown as Res) : data;
    }
    return this.callAction<_Req, Res>(actionInfo);
  }

  /** Ask a live paged query's page again (a row of it went away and it has more behind it). */
  private refillPage = (watchedQuery: WatchedQuery<T>): void => {
    void this._query(watchedQuery.request)
      .then((rows) => watchedQuery.updateResults(rows))
      .catch((error) => console.warn(`[FlowSync] ${watchedQuery.request.type}: page refill failed`, error));
  };

  /** The `has_more` / `next_offset` / `next_cursor` of a live paged query, or undefined for an unpaged one. */
  public getQueryPage(request: QueryRequest): ListPage | undefined {
    const watchedQuery = this.watchedQueries.getWatchedQuery(request);
    if (watchedQuery?.hasMore === undefined) return undefined;
    return {
      has_more: watchedQuery.hasMore,
      next_offset: watchedQuery.nextOffset ?? 0,
      next_cursor: watchedQuery.nextCursor ?? null,
    };
  }

  public getCachedQueryResults<U extends T>(request: QueryRequest): U[] | undefined {
    const watchedQuery = this.watchedQueries.getWatchedQuery(request);
    return watchedQuery?.results as U[] | undefined;
  }

  public async watchQuery<U extends T>(request: QueryRequest): Promise<() => void> {
    if (!request.callback) {
      throw new Error('QueryRequest must have a callback for watchQuery');
    }

    // A watch is push-only: the fetch below seeds it, and every LATER change
    // arrives over the WebSocket. Registering one without a socket is the silent
    // failure this guards -- the caller gets an unsubscribe handle, the list
    // renders once, and it never updates again. Served micro-apps hit exactly
    // that: `initSdk` starts no subscriptions, so nothing opened the channel.
    //
    // Asked for HERE, by the thing that needs it, rather than at boot: `connect`
    // short-circuits on OPEN/CONNECTING so repeat calls are free, a page that
    // never watches anything still opens no socket, and it cannot be defeated by
    // init ordering. Not awaited -- the seed fetch must not wait on a handshake.
    // `.catch`, not `void`: unawaited by design, but a rejected promise nobody
    // observes is an unhandled rejection. A failed handshake here is already
    // reported by the connection status; it must not take the process down.
    ConnectionManager.getInstance()
      .connect()
      .catch(() => undefined);

    // Check if a WatchedQuery exists
    const watchedQuery = this.watchedQueries.getWatchedQuery(request);
    let queryResult: U[];

    if (!watchedQuery) {
      // No cache exists - fetch from API
      queryResult = await this._query<U>(request);
    } else if (watchedQuery.pendingPromise) {
      // In-flight request - await it
      queryResult = (await watchedQuery.pendingPromise) as U[];
    } else {
      // Cache exists with results - use it (even if results are empty/null)
      queryResult = watchedQuery.results as U[];
    }

    const removeWatchCallback = this.watchedQueries.registerWatch(request, queryResult);
    void (request.callback as (entities: U[]) => void | Promise<void>)(queryResult);

    // Return callback to remove watch. A query's rows are held by it; once its last
    // watcher leaves, the finished ones among them have nothing keeping them.
    return () => {
      const rows = this.watchedQueries.getWatchedQuery(request)?.results;
      removeWatchCallback();
      if (!rows || this.watchedQueries.getWatchedQuery(request)) return;
      for (const row of [...rows]) this.evictIfUnreferenced(row.typeId);
    };
  }

  // DEPRECATED: Use watchQuery instead
  // subscribeQuery has been removed to simplify the architecture.
  // watchQuery now handles both initial fetch and subscription.

  public async query<U extends T>(request: QueryRequest, invalidate: boolean = false): Promise<U[]> {
    // If invalidate is false, check cache before querying
    if (!invalidate) {
      // Check cache before querying
      const watchedQuery = this.watchedQueries.getWatchedQuery(request);
      if (watchedQuery) {
        // Check if there's an in-flight request
        if (watchedQuery.pendingPromise) {
          return (await watchedQuery.pendingPromise) as U[];
        }

        // Check if we have cached results
        if (watchedQuery.results !== undefined) {
          return watchedQuery.results as U[];
        }
      }
    }

    return await this._query<U>(request);
  }

  private async _query<U extends T>(request: QueryRequest): Promise<U[]> {
    const { type, query, scope } = request;
    // A type this server does not serve has no rows here: answer empty instead of asking.
    if (!this.servesType(type)) {
      if (!this.unservedTypesLogged.has(type)) {
        this.unservedTypesLogged.add(type);
        console.warn(
          `[FlowSync] ${type}: not served by this backend (absent from bootstrap types); queries answer empty`,
        );
      }
      return [];
    }
    const epoch = this.queryEpoch;

    // Check if WatchedQuery with pending promise already exists (another call beat us to it)
    const existingWatchedQuery = this.watchedQueries.getWatchedQuery(request);
    if (existingWatchedQuery?.pendingPromise) {
      return (await existingWatchedQuery.pendingPromise) as U[];
    }

    // Create the promise that will be stored in WatchedQuery
    const idBatch = this.claimIdBatch(type, query);
    const queryPromise = (async (): Promise<U[]> => {
      // Check all scope entities are saved
      for (const parent_type_id of scope) {
        const parent_ref = this.entities.get(parent_type_id);
        if (parent_ref && parent_ref.entity && !parent_ref.entity.saved) {
          try {
            await this.fetchByTypeId(parent_type_id);
          } catch (error) {
            const httpStatus =
              (error as { response?: { status?: number }; status?: number })?.response?.status ??
              (error as { status?: number })?.status;
            if (httpStatus === 404) {
              parent_ref.status = EntityStatus.ERROR;
              parent_ref.notFound = true;
              return [];
            }
            throw error;
          }
          const refreshedParent = this.getByTypeIdFromCache(parent_type_id);
          if (refreshedParent && !refreshedParent.saved) {
            return [];
          }
        }
      }

      // A test on a field this server does not keep is answered here, not sent (the server refuses it).
      const asked = query ? query.forServer(this.keptFields(type)) : undefined;
      if (asked === null) {
        const none: U[] = [];
        return query?.limit != null
          ? withPage(none, { has_more: false, next_offset: query.offset ?? 0, next_cursor: null })
          : none;
      }
      const apiQuery: any = asked ? asked.toJSON() : {};
      // Blob-carrying types (comment.raw_content, …) serve their blob fields
      // only under expand=blobs — without this, a scoped query returns rows
      // with EMPTY bodies (a receiver's synced comment renders "(empty)" in
      // the gutter; the author only sees text via their in-memory copy).
      // Mirrors getLoadingExpansions' hasBlobs rule on the by-id load path.
      if (!apiQuery.expand && this.getSchema(type)?.hasBlobs) {
        apiQuery.expand = 'blobs';
      }
      let scope_path = '';
      for (const parent_type_id of scope) {
        scope_path = `${scope_path}/${parent_type_id.type}/${parent_type_id.id}`;
      }
      const endpoint = `${GRAPH_API_PREFIX}${scope_path}/${type}`;
      const entitiesJson: IEntity[] = (await apiClient.get<IEntity[]>(endpoint, {
        params: apiQuery,
      })) as unknown as IEntity[];
      if (epoch !== this.queryEpoch) throw new Error('SDK scope changed');
      const queryResult: U[] = [];
      for (const entityJson of entitiesJson) {
        if (!entityJson['type'] || !entityJson['id']) {
          console.warn(`[DataManager] Skipping entity with missing type or id during query for type: ${type}`);
          continue;
        }
        const constructor = EntityFactory.getEntityConstructor(entityJson['type']);
        if (!constructor) {
          console.warn(`[DataManager] Skipping entity, constructor not found for type: ${entityJson['type']}`);
          continue;
        }
        // Use same identifier logic as APIEntity.identifier to ensure consistent cache lookups
        const identifier = entityJson['uname'] ? `@${entityJson['uname']}` : entityJson['id'];
        // An unparseable id must not enter the cache — the same check the TypeId
        // constructor makes, without allocating one per row to throw it away.
        if (!isValidIdentifier(identifier)) {
          console.warn(`[DataManager] Skipping entity with invalid id "${identifier}" for type: ${entityJson['type']}`);
          continue;
        }
        const entity = this.castAndDeepAssign(entityJson);
        this.register_new_entity(entity.typeId, entity);
        const ref = this.getRef(entity.typeId);
        // A data-op that landed while the batch owned this ref was buffered.
        if (idBatch.has(ref)) this.applyPendingUpdate(entity.typeId, ref);
        queryResult.push(ref.entity as U);
      }

      // A paged answer says where the next page starts; the rows keep it.
      const page = pageOf(entitiesJson);
      return page ? withPage(queryResult, page) : queryResult;
    })().finally(() => this.settleIdBatch(idBatch));

    // Register the pending promise BEFORE awaiting it
    this.watchedQueries.registerWatchResults(request, undefined as any, queryPromise);

    // Now await the promise
    try {
      const results = await queryPromise;

      // Update the WatchedQuery with final results and clear pending promise
      const watchedQuery = this.watchedQueries.getWatchedQuery(request);
      if (epoch === this.queryEpoch && watchedQuery?.pendingPromise === queryPromise) {
        watchedQuery.results = results;
        watchedQuery.setPage(results);
        watchedQuery.pendingPromise = undefined;
      }
      this.watchedQueries.releaseIfUnwatched(request);
      return results;
    } catch (error) {
      // Clear pending promise on error
      const watchedQuery = this.watchedQueries.getWatchedQuery(request);
      if (epoch === this.queryEpoch && watchedQuery?.pendingPromise === queryPromise) {
        watchedQuery.pendingPromise = undefined;
      }
      this.watchedQueries.releaseIfUnwatched(request);
      throw error;
    }
  }

  subscribe<U extends T>(
    typeId: TypeId,
    callback?: (entity: U | null) => void | Promise<void>,
    // TODO [FLOWPAD-1059] Remove initialFetch and use useQuery instead
    initialFetch: boolean = false,
  ): () => void {
    if (!this.subscriptions.has(typeId)) {
      this.subscriptions.set(typeId, new Set());
    }
    const callbacks = this.subscriptions.get(typeId);
    if (callback) callbacks?.add(callback as (entity: T | null) => void | Promise<void>);
    if (initialFetch) {
      // Immediately call new callback
      const cached = this.getByTypeIdFromCache<U>(typeId);
      void callback?.(cached);
      // Initiate fetch if entity is not in cache
      if (!cached) {
        void this.refreshByTypeId(typeId);
      }
    }
    // Return unsubscribe function
    return () => {
      if (callback) callbacks?.delete(callback as (entity: T | null) => void | Promise<void>);
      if (callbacks?.size === 0 && this.subscriptions.get(typeId) === callbacks) this.subscriptions.delete(typeId);
      this.evictIfUnreferenced(typeId);
    };
  }

  async watch(typeId: TypeId): Promise<() => Promise<void>> {
    const watchersCount = this.watches.get(typeId) || 0;
    this.watches.set(typeId, watchersCount + 1);
    if (watchersCount === 0) await this.postWatch(typeId);

    return async () => {
      const watchersCount = this.watches.get(typeId);
      if (!watchersCount) {
        return;
      }
      this.watches.set(typeId, watchersCount - 1);
      if (watchersCount - 1 <= 0) {
        this.watches.delete(typeId);
        // Unwatch first: it tells the backend only while the entity is still cached.
        await this.unwatch(typeId);
        this.evictIfUnreferenced(typeId);
      }
    };
  }

  /**
   * Tell the backend this connection watches the key. One request per key at a time, kept
   * while it is open so an `/unwatch` for the same key is sent after it, never across it.
   * Never rejects: a failed registration is repeated on the next reconnect.
   */
  private postWatch(typeId: TypeId): Promise<void> {
    const open = this.watchPosts.get(typeId);
    if (open) return open;
    const connection_manager = ConnectionManager.getInstance();
    if (!connection_manager.connected) return Promise.resolve();
    const request = apiClient
      .post<IEntity>(`${config.API_PREFIXES.graph}/${typeId.type}/${typeId.id}/watch`, {
        connection_id: connection_manager.id,
      })
      .then(
        () => undefined,
        (e: unknown) => {
          console.warn(`[Store] watch POST failed for ${typeId.toString()}, will retry on reconnect:`, e);
        },
      )
      .finally(() => {
        if (this.watchPosts.get(typeId) === request) this.watchPosts.delete(typeId);
      });
    this.watchPosts.set(typeId, request);
    return request;
  }

  private async unwatch(typeId: TypeId) {
    await this.watchPosts.get(typeId);
    // Someone took the key again while its registration was still open: it stays watched.
    if ((this.watches.get(typeId) ?? 0) > 0) return;
    if (!this.getByTypeIdFromCache(typeId)) {
      // Entity is not in cache, so it is probably deleted
      return;
    }
    const connection_manager = ConnectionManager.getInstance();
    if (connection_manager.connected) {
      const connection_id = connection_manager.id;
      try {
        await apiClient.post(`${config.API_PREFIXES.graph}/${typeId.type}/${typeId.id}/unwatch`, {
          connection_id: connection_id,
        });
      } catch {
        // Best-effort teardown. The cache check above only catches a row this
        // client already dropped; a row deleted SERVER-side is still cached
        // here, so the unwatch 404s. The caller is the unsubscribe function
        // React runs on unmount and never awaits, so a rejection here is an
        // unhandled rejection that fails the whole run even when every test
        // passed — same shape as the fire-and-forget connect. The watch is
        // gone either way: the row is gone, and the socket drops with it.
      }
    }
  }

  public async delete(typeId: TypeId): Promise<void> {
    const ref = this.entities.get(typeId);
    if (!ref) {
      throw new Error('Can not delete, Entity not defined');
    }
    const entity = ref.entity;
    if (!entity) {
      throw new Error('Can not delete, Empty ref entity');
    }

    try {
      let isDeleted: boolean = true;
      if (entity.saved) {
        const endpoint = `${GRAPH_API_PREFIX}/${typeId.type}/${typeId.id}`;
        console.log(`🌐 [DataManager.delete] Making DELETE request to ${endpoint}`);
        isDeleted = (await apiClient.delete<IEntity>(endpoint)) as unknown as boolean;
        console.log(`📡 [DataManager.delete] API response for ${typeId.type}:${typeId.id}, deleted: ${isDeleted}`);
      } else {
        console.log(`📝 [DataManager.delete] Entity not saved, skipping API call for ${typeId.type}:${typeId.id}`);
      }
      if (!isDeleted) {
        throw new Error('No data returned');
      }
      this._evict(typeId);
      // Keep query-driven UIs reactive even when backend doesn't emit a delete DataOp.
      // If a delete DataOp arrives later, removeEntityFromResults is idempotent.
      this.watchedQueries.removeEntityFromResults(typeId.type, typeId, this.refillPage);
      console.log(`🗂️ [DataManager.delete] Removed ${typeId.type}:${typeId.id} from local entities cache`);
      console.log(`🔔 [DataManager.delete] Waiting for DataOp from server for query invalidation...`);
    } catch (error) {
      console.error(`❌ [DataManager.delete] Error deleting entity by ID: ${typeId.toString()}`, error);
      ref.status = EntityStatus.ERROR;
      if (isApiError(error)) {
        ref.error = error;
      }
      throw error;
    }
  }

  private static getTypeOfObject(obj: any) {
    if (typeof obj !== 'object' || obj === null) {
      return undefined;
    }
    // Object may be a proxy or a json object.
    //
    // Test the VALUE, not key presence. `APIEntity` DECLARES `type?: string`,
    // and `ui/tsconfig.app.json` compiles with `useDefineForClassFields: true`
    // (ts_sdk's own tsconfig has it false), so under the UI build every
    // instance gets an own `type` property initialised to `undefined`.
    // `'type' in obj` is therefore always true there, and a key-presence check
    // returned `undefined` for any entity built in code rather than from the
    // wire — `new Project({id, name})` threw "Invalid entity type or ID".
    // The static the class registers under is the right answer in that case.
    if (obj.type) {
      return obj.type;
    } else if ('constructor' in obj && 'type' in obj.constructor) {
      return obj.constructor.type;
    }
    return undefined;
  }

  public async getCurrentUser(): Promise<any> {
    try {
      let currentUser = (await apiClient.get<any>(`${config.API_PREFIXES.currentUser}`)) as unknown as any;
      currentUser = this.castAndDeepAssign(currentUser);
      return currentUser;
    } catch {
      return null;
    }
  }

  public castAndDeepAssign<U extends T>(source: any) {
    const entityType = DataManager.getTypeOfObject(source);

    if (!entityType || !('id' in source)) {
      throw new Error('Invalid entity type or ID');
    }
    // Use same identifier logic as APIEntity.identifier to ensure consistent cache lookups
    const sourceIdentifier = source.uname ? `@${source.uname}` : source.id;
    const sourceTypeId = new TypeId(entityType, sourceIdentifier);
    const cachedSource = this.getByTypeIdFromCache<U>(sourceTypeId);
    if (cachedSource) {
      source.expand = this.mergeExpansions(cachedSource.expand, source.expand);

      const maybeOnEntityUpdate = (cachedSource as any).onEntityUpdate;
      const hasEntityUpdateHook = typeof maybeOnEntityUpdate === 'function';
      if (hasEntityUpdateHook) {
        try {
          maybeOnEntityUpdate.call(cachedSource, source);
        } catch (error) {
          console.warn(`[DataManager.castAndDeepAssign] onEntityUpdate failed for ${sourceTypeId.toString()}:`, error);
        }
      }

      // When entities provide their own update hook, avoid clobbering normalized
      // state fields with raw snake_case payloads.
      if (hasEntityUpdateHook && source && typeof source === 'object' && 'state' in source) {
        const { state: _ignoredState, ...rest } = source;
        this.deepAssign(cachedSource, rest);
      } else {
        this.deepAssign(cachedSource, source);
      }
      // ``deepAssign`` re-adds the raw ``shared_context_entities`` /
      // ``private_context_entities`` string arrays without the constructor's
      // string→TypeId parse, leaving the internal ``_shared_context_entities_``
      // / ``_private_context_entities_`` arrays (which the getters read) stale.
      this._rehydrateContextEntities(cachedSource, source);
      cachedSource.dirty = false;
      return cachedSource;
    }
    const entityConstructor = EntityFactory.getEntityConstructor(entityType);
    if (!entityConstructor) {
      throw new Error(`Entity constructor not found for type: ${entityType}`);
    }
    return new entityConstructor(source) as U;
  }

  /**
   * Loads an entity from JSON data into the DataManager cache
   * Creates entity instance and registers it as if fetched from API
   */
  public updateEntityFromJson<U extends T>(entityJson: any): U {
    const entity = this.castAndDeepAssign<U>(entityJson);
    // Register the entity in cache if it's not already there
    if (!this.getByTypeIdFromCache(entity.typeId)) {
      this.register_new_entity(entity.typeId, entity);
    }
    return entity;
  }

  /**
   * Resolve the single entity whose `asset_ref` equals `path` — a PURE index
   * lookup (`GET /assets/entity`, backed by `Entity.get_by_asset_ref`). No
   * recovery, no discovery scan, no indexing — returns the entity or null.
   * The cheap path→entity conversion (e.g. minting a vfs asset tab's project);
   * `systemTools.resolveByPath` is the classifying counterpart, used only by
   * the editor view on a miss. Hydrates + caches the hit via the standard
   * dedup path.
   *
   * Accepts both machine-absolute paths and slash-less VFS sub-paths: stored
   * `asset_ref` is the machine-absolute form, so a relative-looking path is
   * prefixed with `/` here — the single choke point — rather than at each
   * call site.
   */
  public async getEntityByPath<U extends T>(path: string): Promise<U | null> {
    const machine = isAbsoluteMachinePath(path) ? path : `/${path}`;
    const json = await apiClient.get<any>('/assets/entity', { params: { path: machine } }).catch(() => null);
    if (!json) return null;
    return this.updateEntityFromJson<U>(json);
  }

  public deepAssign(target: any, source: any) {
    for (const key in source) {
      if (Array.isArray(source[key])) {
        // An array in a payload is the complete list, never a partial update.
        // Merging it by index kept the cached tail whenever it shrank — a
        // message's pre-download `body_missing_attachments: [x]` survived the
        // post-download `[]`, so a fully downloaded message read as "arrived short".
        target[key] = this.deepAssign([], source[key]);
      } else if (typeof source[key] === 'object' && source[key] !== null) {
        if (!target[key]) {
          target[key] = {};
        }
        target[key] = this.deepAssign(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
    return target;
  }

  /**
   * Re-parse wire-bound context arrays on update. APIEntity's constructor
   * converts ``shared_context_entities`` / ``private_context_entities`` from
   * string typeids to ``TypeId`` instances stored in ``_shared_context_entities_``
   * / ``_private_context_entities_`` and deletes the raw fields. ``deepAssign``
   * doesn't re-run the constructor, so an incoming UPDATE that ships new
   * ``shared_context_entities`` strings leaves the internal arrays stale.
   * Call this after ``deepAssign`` to keep them in sync.
   *
   * Only acts when the incoming ``source`` carries the raw field (so we don't
   * clobber a previously-populated internal array on partial updates).
   */
  private _rehydrateContextEntities(target: any, source: any): void {
    if (!source || typeof source !== 'object') return;
    const rehydrate = (wireField: string, internalField: string) => {
      if (!(wireField in source)) return;
      const wireValue = source[wireField] ?? target[wireField];
      if (!Array.isArray(wireValue)) return;
      target[internalField] = wireValue.map((v: unknown) => (v instanceof TypeId ? v : new TypeId(String(v))));
      delete target[wireField];
    };
    rehydrate('shared_context_entities', '_shared_context_entities_');
    rehydrate('private_context_entities', '_private_context_entities_');
  }

  private mergeArrays<T>(arr1?: T[] | null, arr2?: T[] | null): T[] | null {
    if (!arr1 && !arr2) return null;
    return [...new Set([...(arr1 ?? []), ...(arr2 ?? [])])];
  }

  private mergeExpansions(exp1?: EntityExpansion, exp2?: EntityExpansion): EntityExpansion {
    // TODO [FLOWPAD-891] Delete mergeExpansions and don't use entity.expand.expansions array
    // Per-row facts a type adds to its permissions expansion (`mine`, an llm_endpoint's tree facts)
    // are kept, newest first: rebuilding from the generic fields alone dropped them whenever the
    // entity was already cached, so a re-read lost its own answers.
    return {
      ...exp1,
      ...exp2,
      roles: this.mergeArrays(exp1?.roles, exp2?.roles),
      allowed_actions: this.mergeArrays(exp1?.allowed_actions, exp2?.allowed_actions),
      auth_scopes: this.mergeArrays(exp1?.auth_scopes, exp2?.auth_scopes),
      is_private: exp1?.is_private ?? exp2?.is_private,
      expansions: this.mergeArrays(exp1?.expansions, exp2?.expansions),
    };
  }

  /**
   * Check if this instance matches the global store
   */
  public isSingleton(): boolean {
    //@ts-ignore
    return this === window['store'];
  }

  /**
   * Print a table of all watched queries with their details for debugging
   */
  public watchQueryPrint(): void {
    const tableData: Array<{
      'Instance ID': number;
      Type: string;
      'Callback Name': string;
      'Results Count': number;
    }> = [];

    const allQueries = this.watchedQueries.getAllWatchedQueries();

    // Get all watched queries and iterate through their callbacks
    for (const watchedQuery of allQueries) {
      const callbacks = watchedQuery.getQueryCallbacks();

      if (callbacks.length === 0) {
        // Show watched query even without callbacks
        tableData.push({
          'Instance ID': watchedQuery.instance_id,
          Type: watchedQuery.request.type,
          'Callback Name': '(no callbacks)',
          'Results Count': watchedQuery.results?.length ?? 0,
        });
      } else {
        // Show one row per callback
        for (const queryCallback of callbacks) {
          tableData.push({
            'Instance ID': watchedQuery.instance_id,
            Type: watchedQuery.request.type,
            'Callback Name': queryCallback.name,
            'Results Count': watchedQuery.results?.length ?? 0,
          });
        }
      }
    }

    if (tableData.length === 0) {
      console.log('No watched queries found');
      return;
    }

    console.table(tableData);
    console.log(`Total watched queries: ${this.watchedQueries.getAllWatchedQueries().length}`);
  }
}
